import AssessmentIcon from '@mui/icons-material/Assessment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ClearIcon from '@mui/icons-material/Clear';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import GroupIcon from '@mui/icons-material/Group';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PostAddIcon from '@mui/icons-material/PostAdd';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import {
    Alert,
    Box,
    Button,
    Card,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    Grid,
    InputLabel,
    Skeleton,
    TableCell,
    TableRow,
    TextField,
    ToggleButton,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { darken, lighten } from '@mui/material/styles';
import { alpha, Stack } from '@mui/system';
import { DateRange, LocalizationProvider, StaticDateRangePicker } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { OnlyFansSubscriber } from '../../hooks/useSubscriber';
import { OnlyFansSubscriberCategory } from '../../hooks/useSubscriberCategories';
import useSubscriberPayoutsBalance from '../../hooks/useSubscriberPayoutsBalance';
import useSubscriberPerformerTop from '../../hooks/useSubscriberPerformerTop';
import useSubscriberReferralsBalance from '../../hooks/useSubscriberReferralsBalance';
import useSubscribers from '../../hooks/useSubscribers';
import useSubscriberStatsChargebacksOverallAll, {
    OnlyFansStatisticsChargebacksOverviewAndChartData,
} from '../../hooks/useSubscriberStatsChargebacksOverallAll';
import useSubscriberStatsEarningsOverallAll, {
    OnlyFansStatisticsEarningsOverviewAndChartData,
} from '../../hooks/useSubscriberStatsEarningsOverallAll';
import useSubscriberStatsNewSubscribersCount from '../../hooks/useSubscriberStatsNewSubscribersCount';
import useSubscriberStatsProfileVisitorsAll, {
    OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration,
} from '../../hooks/useSubscriberStatsProfileVisitorsAll';
import useSubscriberStatsTodayActivitySummary from '../../hooks/useSubscriberStatsTodayActivitySummary';
import useSubscriberUsersCounters from '../../hooks/useSubscriberUsersCounters';
import { formatUsdMoney, useAnimatedSummaryNumber } from '../../utils/animatedNumber';
import BackNavigationButton from '../common/BackNavigationButton';
import PageContainer from '../common/PageContainter';
import UsernameWithAvatar from '../common/UsernameWithAvatar';
import AnalyticsControlsBar from './analytics/AnalyticsControlsBar';
import EarningsTrendCard from './analytics/EarningsTrendCard';
import {
    SubscriberCountValue,
    SubscriberCountValueWithComparison,
    SubscriberMetricValueWithComparison,
} from './analytics/MetricValueAtoms';
import ProfileVisitorsCountriesCard from './analytics/ProfileVisitorsCountriesCard';
import ProfileVisitorsSummaryCard from './analytics/ProfileVisitorsSummaryCard';
import ProfileVisitorsTrendCard from './analytics/ProfileVisitorsTrendCard';
import SubscriberAnalyticsCardsSection from './analytics/SubscriberAnalyticsCardsSection';
import SubscriberAnalyticsTableSection from './analytics/SubscriberAnalyticsTableSection';
import { formatRangeLabel, getRangeStartDate, safeNumber, safeTotal, sortSubscribers } from './dashboardAnalytics.utils';
import DashboardAnalyticsSummaryStatCard from './DashboardAnalyticsSummaryStatCard';
import DashboardMyAccountsSortMenuButton from './DashboardMyAccountsSortMenuButton';

type AnalyticsMetricKey =
    | 'earnings'
    | 'chargebacks'
    | 'referralEarnings'
    | 'ltv'
    | 'ltvRevenueBase'
    | 'ltvUsersBase'
    | 'tips'
    | 'messages'
    | 'posts'
    | 'subscriptions'
    | 'streamTips'
    | 'newSubscribersToday'
    | 'activeFans'
    | 'activeFollowing'
    | 'performerTop'
    | 'payoutAvailable'
    | 'payoutPending'
    | 'referralsBalance'
    | 'todayStories'
    | 'todayPosts'
    | 'todayChats'
    | 'todayStreams'
    | 'profileVisitorsTotal'
    | 'profileVisitorsGuests'
    | 'profileVisitorsUsers'
    | 'profileVisitorsSubscribers';
type AnalyticsMetrics = Record<AnalyticsMetricKey, number>;
type AnalyticsChartMetricKey =
    | 'earnings'
    | 'chargebacks'
    | 'referralEarnings'
    | 'tips'
    | 'messages'
    | 'posts'
    | 'subscriptions'
    | 'streamTips';
type AnalyticsChartTotalsKey = keyof Pick<
    AnalyticsMetrics,
    'earnings' | 'chargebacks' | 'referralEarnings' | 'tips' | 'messages' | 'posts' | 'subscriptions' | 'streamTips'
>;
type AnalyticsEarningsDeltaKey = 'earnings' | 'referralEarnings' | 'tips' | 'messages' | 'posts' | 'subscriptions' | 'streamTips';
type AnalyticsDeltas = Record<AnalyticsEarningsDeltaKey, number>;
type AnalyticsAmountType = 'gross' | 'net';
type AnalyticsRangePreset = '7d' | '30d' | '90d' | '1y' | 'all' | 'custom';
type AnalyticsComparisonPreset = 'none' | 'previousPeriod';

const zeroMetrics: AnalyticsMetrics = {
    earnings: 0,
    chargebacks: 0,
    referralEarnings: 0,
    ltv: 0,
    ltvRevenueBase: 0,
    ltvUsersBase: 0,
    tips: 0,
    messages: 0,
    posts: 0,
    subscriptions: 0,
    streamTips: 0,
    newSubscribersToday: 0,
    activeFans: 0,
    activeFollowing: 0,
    performerTop: 0,
    payoutAvailable: 0,
    payoutPending: 0,
    referralsBalance: 0,
    todayStories: 0,
    todayPosts: 0,
    todayChats: 0,
    todayStreams: 0,
    profileVisitorsTotal: 0,
    profileVisitorsGuests: 0,
    profileVisitorsUsers: 0,
    profileVisitorsSubscribers: 0,
};

const extractMetrics = (
    data: OnlyFansStatisticsEarningsOverviewAndChartData.Response | null | undefined,
    amountType: AnalyticsAmountType,
): AnalyticsMetrics => {
    if (!data) {
        return zeroMetrics;
    }

    return {
        earnings: safeTotal(data.total, amountType),
        chargebacks: 0,
        referralEarnings: safeTotal(data.ref, amountType),
        ltv: 0,
        ltvRevenueBase: 0,
        ltvUsersBase: 0,
        tips: safeTotal(data.tips, amountType),
        messages: safeTotal(data.chat_messages, amountType),
        posts: safeTotal(data.post, amountType),
        subscriptions: safeTotal(data.subscribes, amountType),
        streamTips: safeTotal(data.tips_stream, amountType),
        newSubscribersToday: 0,
        activeFans: 0,
        activeFollowing: 0,
        performerTop: 0,
        payoutAvailable: 0,
        payoutPending: 0,
        referralsBalance: 0,
        todayStories: 0,
        todayPosts: 0,
        todayChats: 0,
        todayStreams: 0,
        profileVisitorsTotal: 0,
        profileVisitorsGuests: 0,
        profileVisitorsUsers: 0,
        profileVisitorsSubscribers: 0,
    };
};

const chartMetricToDataKey: Partial<Record<AnalyticsChartMetricKey, keyof OnlyFansStatisticsEarningsOverviewAndChartData.Response>> = {
    earnings: 'total',
    referralEarnings: 'ref',
    tips: 'tips',
    messages: 'chat_messages',
    posts: 'post',
    subscriptions: 'subscribes',
    streamTips: 'tips_stream',
};

const chartMetricLabels: Record<AnalyticsChartMetricKey, string> = {
    earnings: 'Total Earnings',
    chargebacks: 'Total Chargebacks',
    referralEarnings: 'Total Referral Earnings',
    tips: 'Total Tips',
    messages: 'Total Messages',
    posts: 'Total Posts',
    subscriptions: 'Total Subscriptions',
    streamTips: 'Total Stream Tips',
};

const chartMetricToTotalsKey: Record<AnalyticsChartMetricKey, AnalyticsChartTotalsKey> = {
    earnings: 'earnings',
    chargebacks: 'chargebacks',
    referralEarnings: 'referralEarnings',
    tips: 'tips',
    messages: 'messages',
    posts: 'posts',
    subscriptions: 'subscriptions',
    streamTips: 'streamTips',
};

const chartPeakLowLabels: Record<AnalyticsChartMetricKey, { peak: string; lowest: string }> = {
    earnings: { peak: 'Peak Earnings', lowest: 'Lowest Earnings' },
    chargebacks: { peak: 'Peak Chargebacks', lowest: 'Lowest Chargebacks' },
    referralEarnings: { peak: 'Peak Referral Earnings', lowest: 'Lowest Referral Earnings' },
    tips: { peak: 'Peak Tips', lowest: 'Lowest Tips' },
    messages: { peak: 'Peak Messages', lowest: 'Lowest Messages' },
    posts: { peak: 'Peak Posts', lowest: 'Lowest Posts' },
    subscriptions: { peak: 'Peak Subscriptions', lowest: 'Lowest Subscriptions' },
    streamTips: { peak: 'Peak Stream Tips', lowest: 'Lowest Stream Tips' },
};

const extractDeltas = (data: OnlyFansStatisticsEarningsOverviewAndChartData.Response | null | undefined): AnalyticsDeltas => {
    if (!data) {
        return { earnings: 0, referralEarnings: 0, tips: 0, messages: 0, posts: 0, subscriptions: 0, streamTips: 0 };
    }
    return {
        earnings: Number(data.total?.delta || 0),
        referralEarnings: Number(data.ref?.delta || 0),
        tips: Number(data.tips?.delta || 0),
        messages: Number(data.chat_messages?.delta || 0),
        posts: Number(data.post?.delta || 0),
        subscriptions: Number(data.subscribes?.delta || 0),
        streamTips: Number(data.tips_stream?.delta || 0),
    };
};

const FilterTextFiled = ({ filter, setFilter }: any) => {
    const theme = useTheme();
    return (
        <FormControl fullWidth sx={{ mt: 0 }}>
            <InputLabel id="search-label"></InputLabel>
            <TextField
                id="search"
                label="Search"
                value={filter}
                fullWidth
                size="small"
                onChange={e => setFilter(e.target.value)}
                onKeyDown={e => {
                    if (e.key === 'Escape') {
                        setFilter('');
                    }
                }}
                InputProps={{
                    endAdornment: (
                        <ClearIcon
                            onClick={() => setFilter('')}
                            color="error"
                            sx={{ cursor: 'pointer', visibility: filter.length > 0 ? 'visible' : 'hidden' }}
                        />
                    ),
                }}
                sx={{ backgroundColor: theme.palette.background.paper, mb: 0, mt: 0 }}
            />
        </FormControl>
    );
};

const GroupByCategoryToggle = ({ groupByCategory, setGroupByCategory }: any) => (
    <Tooltip title="Toggle grouping subscribers by their assigned category" placement="top">
        <span>
            <ToggleButton
                color="primary"
                size="small"
                value="check"
                selected={groupByCategory}
                onChange={() => setGroupByCategory(!groupByCategory)}
            >
                <GroupIcon fontSize="medium" />
            </ToggleButton>
        </span>
    </Tooltip>
);

const SubscriberAnalyticsRow = ({
    subscriber,
    onLoaded,
    onEarningsDataLoaded,
    onComparisonEarningsDataLoaded,
    onChargebacksDataLoaded,
    onComparisonChargebacksDataLoaded,
    onProfileVisitorsDataLoaded,
    onComparisonProfileVisitorsDataLoaded,
    checked,
    onToggleChecked,
    amountType,
    rangePreset,
    comparisonPreset,
    customRangeStartDate,
    customRangeEndDate,
}: {
    subscriber: OnlyFansSubscriber;
    onLoaded: (
        subscriberId: string,
        metrics: AnalyticsMetrics,
        deltas: AnalyticsDeltas,
        loaded: {
            earnings: boolean;
            chargebacks: boolean;
            newSubscribersToday: boolean;
            usersCounters: boolean;
            performerTop: boolean;
            payoutsBalance: boolean;
            referralsBalance: boolean;
            todayActivity: boolean;
            profileVisitors: boolean;
        },
    ) => void;
    onEarningsDataLoaded: (subscriberId: string, data: OnlyFansStatisticsEarningsOverviewAndChartData.Response | null) => void;
    onComparisonEarningsDataLoaded: (subscriberId: string, data: OnlyFansStatisticsEarningsOverviewAndChartData.Response | null) => void;
    onChargebacksDataLoaded: (subscriberId: string, data: OnlyFansStatisticsChargebacksOverviewAndChartData.Response | null) => void;
    onComparisonChargebacksDataLoaded: (
        subscriberId: string,
        data: OnlyFansStatisticsChargebacksOverviewAndChartData.Response | null,
    ) => void;
    onProfileVisitorsDataLoaded: (
        subscriberId: string,
        data: OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration.Response | null,
    ) => void;
    onComparisonProfileVisitorsDataLoaded: (
        subscriberId: string,
        data: OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration.Response | null,
    ) => void;
    checked: boolean;
    onToggleChecked: (subscriberId: string) => void;
    amountType: AnalyticsAmountType;
    rangePreset: AnalyticsRangePreset;
    comparisonPreset: AnalyticsComparisonPreset;
    customRangeStartDate: Date | null;
    customRangeEndDate: Date | null;
}) => {
    const currentRangeStartDate = useMemo(() => {
        if (rangePreset === 'custom') {
            return customRangeStartDate || undefined;
        }
        return getRangeStartDate(rangePreset);
    }, [rangePreset, customRangeStartDate]);
    const currentRangeEndDate = useMemo(() => {
        if (rangePreset === 'custom') {
            return customRangeEndDate || undefined;
        }
        return moment.utc().endOf('day').toDate();
    }, [rangePreset, customRangeEndDate]);
    const currentRangeQuery = useMemo(() => {
        if (rangePreset === 'all') {
            return { mode: 'all' as const };
        }
        if (rangePreset === 'custom' && customRangeStartDate && customRangeEndDate) {
            return {
                mode: 'range' as const,
                startDate: customRangeStartDate,
                endDate: customRangeEndDate,
            };
        }
        return {
            mode: 'range' as const,
            startDate: currentRangeStartDate,
            endDate: currentRangeEndDate,
        };
    }, [rangePreset, currentRangeStartDate, currentRangeEndDate, customRangeStartDate, customRangeEndDate]);
    const comparisonRangeQuery = useMemo(() => {
        const baseStartDate = rangePreset === 'custom' ? customRangeStartDate : currentRangeStartDate;
        const baseEndDate = rangePreset === 'custom' ? customRangeEndDate : currentRangeEndDate;
        if (comparisonPreset !== 'previousPeriod' || rangePreset === 'all' || !baseStartDate || !baseEndDate) {
            return undefined;
        }
        const days = Math.max(1, moment.utc(baseEndDate).diff(moment.utc(baseStartDate), 'days') + 1);
        const previousEndDate = moment.utc(baseStartDate).subtract(1, 'day').endOf('day').toDate();
        const previousStartDate = moment
            .utc(previousEndDate)
            .subtract(days - 1, 'days')
            .startOf('day')
            .toDate();
        return {
            mode: 'range' as const,
            startDate: previousStartDate,
            endDate: previousEndDate,
        };
    }, [comparisonPreset, rangePreset, currentRangeStartDate, currentRangeEndDate, customRangeStartDate, customRangeEndDate]);
    const { data, isLoading } = useSubscriberStatsEarningsOverallAll(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
        currentRangeQuery,
    );
    const { data: comparisonData } = useSubscriberStatsEarningsOverallAll(
        subscriber.isLoggedIn && comparisonRangeQuery ? String(subscriber._id) : undefined,
        comparisonRangeQuery,
    );
    const { data: chargebacksData, isLoading: isChargebacksLoading } = useSubscriberStatsChargebacksOverallAll(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
        currentRangeQuery,
    );
    const { data: comparisonChargebacksData } = useSubscriberStatsChargebacksOverallAll(
        subscriber.isLoggedIn && comparisonRangeQuery ? String(subscriber._id) : undefined,
        comparisonRangeQuery,
    );
    const { data: usersCounters, isLoading: isUsersCountersLoading } = useSubscriberUsersCounters(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
    );
    const { data: performerTopData, isLoading: isPerformerTopLoading } = useSubscriberPerformerTop(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
    );
    const { data: payoutsBalance, isLoading: isPayoutsBalanceLoading } = useSubscriberPayoutsBalance(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
    );
    const { data: referralsBalance, isLoading: isReferralsBalanceLoading } = useSubscriberReferralsBalance(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
    );
    const { data: todayActivitySummary, isLoading: isTodayActivityLoading } = useSubscriberStatsTodayActivitySummary(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
    );
    const { data: profileVisitorsData, isLoading: isProfileVisitorsLoading } = useSubscriberStatsProfileVisitorsAll(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
        currentRangeQuery,
    );
    const { data: comparisonProfileVisitorsData } = useSubscriberStatsProfileVisitorsAll(
        subscriber.isLoggedIn && comparisonRangeQuery ? String(subscriber._id) : undefined,
        comparisonRangeQuery,
    );
    const performerTopValue = Number(performerTopData?.performerTop || 0);
    const lifetimeNetRevenue = useMemo(() => safeTotal(data?.total, 'net'), [data]);
    const comparisonLifetimeNetRevenue = useMemo(() => safeTotal(comparisonData?.total, 'net'), [comparisonData]);
    const chargebacksValue = useMemo(() => Number(chargebacksData?.total || 0), [chargebacksData]);
    const comparisonChargebacksValue = useMemo(() => Number(comparisonChargebacksData?.total || 0), [comparisonChargebacksData]);
    const ltvUsersBase = Number(usersCounters?.subscribers?.all || 0);
    const ltvRevenueBase = lifetimeNetRevenue - chargebacksValue;
    const comparisonLtvRevenueBase = comparisonLifetimeNetRevenue - comparisonChargebacksValue;
    const ltvValue = ltvUsersBase > 0 ? ltvRevenueBase / ltvUsersBase : 0;
    const comparisonLtvValue = ltvUsersBase > 0 ? comparisonLtvRevenueBase / ltvUsersBase : 0;
    const activeFansValue = Number(usersCounters?.subscribers?.active || 0);
    const activeFollowingValue = Number(usersCounters?.subscriptions?.active || 0);
    const payoutAvailableValue = Number(payoutsBalance?.payoutAvailable || 0);
    const payoutPendingValue = Number(payoutsBalance?.payoutPending || 0);
    const referralsBalanceValue = Number(referralsBalance?.referralEarnings || 0);
    const todayStoriesValue = Number(todayActivitySummary?.stories || 0);
    const todayPostsValue = Number(todayActivitySummary?.posts || 0);
    const todayChatsValue = Number(todayActivitySummary?.chats || 0);
    const todayStreamsValue = Number(todayActivitySummary?.streams || 0);
    const profileVisitorsTotalValue = safeNumber(profileVisitorsData?.topCountries?.totals?.total);
    const profileVisitorsGuestsValue = safeNumber(profileVisitorsData?.topCountries?.totals?.guests);
    const profileVisitorsUsersValue = safeNumber(profileVisitorsData?.topCountries?.totals?.users);
    const profileVisitorsSubscribersValue = safeNumber(profileVisitorsData?.topCountries?.totals?.subscribers);
    const comparisonProfileVisitorsTotalValue = safeNumber(comparisonProfileVisitorsData?.topCountries?.totals?.total);
    const comparisonProfileVisitorsGuestsValue = safeNumber(comparisonProfileVisitorsData?.topCountries?.totals?.guests);
    const comparisonProfileVisitorsUsersValue = safeNumber(comparisonProfileVisitorsData?.topCountries?.totals?.users);
    const comparisonProfileVisitorsSubscribersValue = safeNumber(comparisonProfileVisitorsData?.topCountries?.totals?.subscribers);
    const gmtTodayStartDate = useMemo(() => moment.utc().startOf('day').toDate(), []);
    const gmtTodayEndDate = useMemo(() => moment.utc().toDate(), []);
    const { data: newSubscribersTodayCount, isLoading: isNewSubscribersTodayCountLoading } = useSubscriberStatsNewSubscribersCount(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
        gmtTodayStartDate,
        gmtTodayEndDate,
        'new',
    );
    const newSubscribersTodayValue = Number.isFinite(Number(newSubscribersTodayCount ?? 0)) ? Number(newSubscribersTodayCount ?? 0) : 0;
    const metrics = useMemo(() => extractMetrics(data, amountType), [data, amountType]);
    const deltas = useMemo(() => extractDeltas(data), [data]);
    const comparisonMetrics = useMemo(() => extractMetrics(comparisonData, amountType), [comparisonData, amountType]);
    const showComparison = comparisonPreset === 'previousPeriod' && rangePreset !== 'all';

    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onEarningsDataLoaded(String(subscriber._id), data || null);
    }, [subscriber, data, onEarningsDataLoaded]);
    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onComparisonEarningsDataLoaded(String(subscriber._id), comparisonData || null);
    }, [subscriber, comparisonData, onComparisonEarningsDataLoaded]);
    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onChargebacksDataLoaded(String(subscriber._id), chargebacksData || null);
    }, [subscriber, chargebacksData, onChargebacksDataLoaded]);
    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onComparisonChargebacksDataLoaded(String(subscriber._id), comparisonChargebacksData || null);
    }, [subscriber, comparisonChargebacksData, onComparisonChargebacksDataLoaded]);
    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onProfileVisitorsDataLoaded(String(subscriber._id), profileVisitorsData || null);
    }, [subscriber, profileVisitorsData, onProfileVisitorsDataLoaded]);
    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onComparisonProfileVisitorsDataLoaded(String(subscriber._id), comparisonProfileVisitorsData || null);
    }, [subscriber, comparisonProfileVisitorsData, onComparisonProfileVisitorsDataLoaded]);
    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        if (!subscriber.isLoggedIn) {
            onLoaded(String(subscriber._id), zeroMetrics, extractDeltas(null), {
                earnings: true,
                chargebacks: true,
                newSubscribersToday: true,
                usersCounters: true,
                performerTop: true,
                payoutsBalance: true,
                referralsBalance: true,
                todayActivity: true,
                profileVisitors: true,
            });
            return;
        }

        onLoaded(
            String(subscriber._id),
            {
                ...metrics,
                chargebacks: chargebacksValue,
                ltv: ltvValue,
                ltvRevenueBase,
                ltvUsersBase,
                newSubscribersToday: Number.isFinite(Number(newSubscribersTodayCount ?? 0)) ? Number(newSubscribersTodayCount ?? 0) : 0,
                activeFans: activeFansValue,
                activeFollowing: activeFollowingValue,
                performerTop: performerTopValue,
                payoutAvailable: payoutAvailableValue,
                payoutPending: payoutPendingValue,
                referralsBalance: referralsBalanceValue,
                todayStories: todayStoriesValue,
                todayPosts: todayPostsValue,
                todayChats: todayChatsValue,
                todayStreams: todayStreamsValue,
                profileVisitorsTotal: profileVisitorsTotalValue,
                profileVisitorsGuests: profileVisitorsGuestsValue,
                profileVisitorsUsers: profileVisitorsUsersValue,
                profileVisitorsSubscribers: profileVisitorsSubscribersValue,
            },
            deltas,
            {
                earnings: !isLoading,
                chargebacks: !isChargebacksLoading,
                newSubscribersToday: !isNewSubscribersTodayCountLoading,
                usersCounters: !isUsersCountersLoading,
                performerTop: !isPerformerTopLoading,
                payoutsBalance: !isPayoutsBalanceLoading,
                referralsBalance: !isReferralsBalanceLoading,
                todayActivity: !isTodayActivityLoading,
                profileVisitors: !isProfileVisitorsLoading,
            },
        );
    }, [
        subscriber,
        metrics,
        chargebacksValue,
        deltas,
        ltvValue,
        ltvRevenueBase,
        ltvUsersBase,
        isLoading,
        isChargebacksLoading,
        usersCounters,
        isUsersCountersLoading,
        payoutAvailableValue,
        payoutPendingValue,
        isPayoutsBalanceLoading,
        referralsBalanceValue,
        isReferralsBalanceLoading,
        todayStoriesValue,
        todayPostsValue,
        todayChatsValue,
        todayStreamsValue,
        isTodayActivityLoading,
        profileVisitorsTotalValue,
        profileVisitorsGuestsValue,
        profileVisitorsUsersValue,
        profileVisitorsSubscribersValue,
        isProfileVisitorsLoading,
        performerTopData,
        isPerformerTopLoading,
        newSubscribersTodayCount,
        isNewSubscribersTodayCountLoading,
        onLoaded,
        onProfileVisitorsDataLoaded,
        onComparisonProfileVisitorsDataLoaded,
    ]);

    return (
        <TableRow>
            <TableCell
                className="sticky-cell sticky-cell-first"
                padding="checkbox"
                sx={{ position: 'sticky', left: 0, zIndex: 3, width: 42, minWidth: 42, backgroundColor: 'background.paper' }}
            >
                <Checkbox
                    checked={checked}
                    onChange={() => onToggleChecked(String(subscriber._id))}
                    inputProps={{ 'aria-label': `Select ${subscriber.username}` }}
                />
            </TableCell>
            <TableCell
                className="sticky-cell"
                sx={{ minWidth: 260, position: 'sticky', left: 42, zIndex: 2, backgroundColor: 'background.paper' }}
            >
                <UsernameWithAvatar
                    subscriber={subscriber}
                    category={subscriber?.cognito?.categoryDetails?.[0]}
                    refetchSubscribers={() => {}}
                    showLocalMenu={false}
                    enableLoginActions={false}
                    showCategoryMoveAction={false}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberCountValue value={newSubscribersTodayValue} isLoading={isNewSubscribersTodayCountLoading} />
            </TableCell>
            <TableCell align="right">
                <SubscriberCountValue value={activeFansValue} isLoading={isUsersCountersLoading} />
            </TableCell>
            <TableCell align="right">
                <SubscriberCountValue value={activeFollowingValue} isLoading={isUsersCountersLoading} />
            </TableCell>
            <TableCell align="right">
                {isPerformerTopLoading ? (
                    <Skeleton width={40} sx={{ ml: 'auto' }} />
                ) : (
                    <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '1.02rem', fontWeight: 600 }}>
                        {performerTopValue.toFixed(2)}%
                    </Typography>
                )}
            </TableCell>
            <TableCell align="right">
                <SubscriberMetricValueWithComparison
                    value={metrics.earnings}
                    comparisonValue={comparisonMetrics.earnings}
                    isLoading={isLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberMetricValueWithComparison
                    value={chargebacksValue}
                    comparisonValue={comparisonChargebacksValue}
                    isLoading={isChargebacksLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberMetricValueWithComparison
                    value={ltvValue}
                    comparisonValue={comparisonLtvValue}
                    isLoading={isLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberMetricValueWithComparison
                    value={metrics.tips}
                    comparisonValue={comparisonMetrics.tips}
                    isLoading={isLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberMetricValueWithComparison
                    value={metrics.messages}
                    comparisonValue={comparisonMetrics.messages}
                    isLoading={isLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberMetricValueWithComparison
                    value={metrics.posts}
                    comparisonValue={comparisonMetrics.posts}
                    isLoading={isLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberMetricValueWithComparison
                    value={metrics.subscriptions}
                    comparisonValue={comparisonMetrics.subscriptions}
                    isLoading={isLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberMetricValueWithComparison
                    value={metrics.streamTips}
                    comparisonValue={comparisonMetrics.streamTips}
                    isLoading={isLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberMetricValueWithComparison
                    value={metrics.referralEarnings}
                    comparisonValue={comparisonMetrics.referralEarnings}
                    isLoading={isLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                {isPayoutsBalanceLoading ? (
                    <Skeleton width={72} sx={{ ml: 'auto' }} />
                ) : (
                    <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '1.02rem', fontWeight: 600 }}>
                        {formatUsdMoney(payoutAvailableValue)}
                    </Typography>
                )}
            </TableCell>
            <TableCell align="right">
                {isPayoutsBalanceLoading ? (
                    <Skeleton width={72} sx={{ ml: 'auto' }} />
                ) : (
                    <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '1.02rem', fontWeight: 600 }}>
                        {formatUsdMoney(payoutPendingValue)}
                    </Typography>
                )}
            </TableCell>
            <TableCell align="right">
                {isReferralsBalanceLoading ? (
                    <Skeleton width={72} sx={{ ml: 'auto' }} />
                ) : (
                    <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '1.02rem', fontWeight: 600 }}>
                        {formatUsdMoney(referralsBalanceValue)}
                    </Typography>
                )}
            </TableCell>
            <TableCell align="right">
                <SubscriberCountValue value={todayStoriesValue} isLoading={isTodayActivityLoading} />
            </TableCell>
            <TableCell align="right">
                <SubscriberCountValue value={todayPostsValue} isLoading={isTodayActivityLoading} />
            </TableCell>
            <TableCell align="right">
                <SubscriberCountValue value={todayChatsValue} isLoading={isTodayActivityLoading} />
            </TableCell>
            <TableCell align="right">
                <SubscriberCountValue value={todayStreamsValue} isLoading={isTodayActivityLoading} />
            </TableCell>
            <TableCell align="right">
                <SubscriberCountValueWithComparison
                    value={profileVisitorsTotalValue}
                    comparisonValue={comparisonProfileVisitorsTotalValue}
                    isLoading={isProfileVisitorsLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberCountValueWithComparison
                    value={profileVisitorsGuestsValue}
                    comparisonValue={comparisonProfileVisitorsGuestsValue}
                    isLoading={isProfileVisitorsLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberCountValueWithComparison
                    value={profileVisitorsUsersValue}
                    comparisonValue={comparisonProfileVisitorsUsersValue}
                    isLoading={isProfileVisitorsLoading}
                    showComparison={showComparison}
                />
            </TableCell>
            <TableCell align="right">
                <SubscriberCountValueWithComparison
                    value={profileVisitorsSubscribersValue}
                    comparisonValue={comparisonProfileVisitorsSubscribersValue}
                    isLoading={isProfileVisitorsLoading}
                    showComparison={showComparison}
                />
            </TableCell>
        </TableRow>
    );
};

const SubscriberAnalyticsCard = ({
    subscriber,
    onLoaded,
    onEarningsDataLoaded,
    onComparisonEarningsDataLoaded,
    onChargebacksDataLoaded,
    onComparisonChargebacksDataLoaded,
    onProfileVisitorsDataLoaded,
    onComparisonProfileVisitorsDataLoaded,
    checked,
    onToggleChecked,
    amountType,
    rangePreset,
    comparisonPreset,
    customRangeStartDate,
    customRangeEndDate,
}: {
    subscriber: OnlyFansSubscriber;
    onLoaded: (
        subscriberId: string,
        metrics: AnalyticsMetrics,
        deltas: AnalyticsDeltas,
        loaded: {
            earnings: boolean;
            chargebacks: boolean;
            newSubscribersToday: boolean;
            usersCounters: boolean;
            performerTop: boolean;
            payoutsBalance: boolean;
            referralsBalance: boolean;
            todayActivity: boolean;
            profileVisitors: boolean;
        },
    ) => void;
    onEarningsDataLoaded: (subscriberId: string, data: OnlyFansStatisticsEarningsOverviewAndChartData.Response | null) => void;
    onComparisonEarningsDataLoaded: (subscriberId: string, data: OnlyFansStatisticsEarningsOverviewAndChartData.Response | null) => void;
    onChargebacksDataLoaded: (subscriberId: string, data: OnlyFansStatisticsChargebacksOverviewAndChartData.Response | null) => void;
    onComparisonChargebacksDataLoaded: (
        subscriberId: string,
        data: OnlyFansStatisticsChargebacksOverviewAndChartData.Response | null,
    ) => void;
    onProfileVisitorsDataLoaded: (
        subscriberId: string,
        data: OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration.Response | null,
    ) => void;
    onComparisonProfileVisitorsDataLoaded: (
        subscriberId: string,
        data: OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration.Response | null,
    ) => void;
    checked: boolean;
    onToggleChecked: (subscriberId: string) => void;
    amountType: AnalyticsAmountType;
    rangePreset: AnalyticsRangePreset;
    comparisonPreset: AnalyticsComparisonPreset;
    customRangeStartDate: Date | null;
    customRangeEndDate: Date | null;
}) => {
    const showComparison = comparisonPreset === 'previousPeriod' && rangePreset !== 'all';
    const currentRangeStartDate = useMemo(() => {
        if (rangePreset === 'custom') {
            return customRangeStartDate || undefined;
        }
        return getRangeStartDate(rangePreset);
    }, [rangePreset, customRangeStartDate]);
    const currentRangeEndDate = useMemo(() => {
        if (rangePreset === 'custom') {
            return customRangeEndDate || undefined;
        }
        return moment.utc().endOf('day').toDate();
    }, [rangePreset, customRangeEndDate]);
    const currentRangeQuery = useMemo(() => {
        if (rangePreset === 'all') {
            return { mode: 'all' as const };
        }
        if (rangePreset === 'custom' && customRangeStartDate && customRangeEndDate) {
            return {
                mode: 'range' as const,
                startDate: customRangeStartDate,
                endDate: customRangeEndDate,
            };
        }
        return {
            mode: 'range' as const,
            startDate: currentRangeStartDate,
            endDate: currentRangeEndDate,
        };
    }, [rangePreset, currentRangeStartDate, currentRangeEndDate, customRangeStartDate, customRangeEndDate]);
    const comparisonRangeQuery = useMemo(() => {
        const baseStartDate = rangePreset === 'custom' ? customRangeStartDate : currentRangeStartDate;
        const baseEndDate = rangePreset === 'custom' ? customRangeEndDate : currentRangeEndDate;
        if (comparisonPreset !== 'previousPeriod' || rangePreset === 'all' || !baseStartDate || !baseEndDate) {
            return undefined;
        }
        const days = Math.max(1, moment.utc(baseEndDate).diff(moment.utc(baseStartDate), 'days') + 1);
        const previousEndDate = moment.utc(baseStartDate).subtract(1, 'day').endOf('day').toDate();
        const previousStartDate = moment
            .utc(previousEndDate)
            .subtract(days - 1, 'days')
            .startOf('day')
            .toDate();
        return {
            mode: 'range' as const,
            startDate: previousStartDate,
            endDate: previousEndDate,
        };
    }, [comparisonPreset, rangePreset, currentRangeStartDate, currentRangeEndDate, customRangeStartDate, customRangeEndDate]);
    const { data, isLoading } = useSubscriberStatsEarningsOverallAll(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
        currentRangeQuery,
    );
    const { data: comparisonData } = useSubscriberStatsEarningsOverallAll(
        subscriber.isLoggedIn && comparisonRangeQuery ? String(subscriber._id) : undefined,
        comparisonRangeQuery,
    );
    const { data: chargebacksData, isLoading: isChargebacksLoading } = useSubscriberStatsChargebacksOverallAll(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
        currentRangeQuery,
    );
    const { data: comparisonChargebacksData } = useSubscriberStatsChargebacksOverallAll(
        subscriber.isLoggedIn && comparisonRangeQuery ? String(subscriber._id) : undefined,
        comparisonRangeQuery,
    );
    const { data: usersCounters, isLoading: isUsersCountersLoading } = useSubscriberUsersCounters(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
    );
    const { data: performerTopData, isLoading: isPerformerTopLoading } = useSubscriberPerformerTop(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
    );
    const { data: payoutsBalance, isLoading: isPayoutsBalanceLoading } = useSubscriberPayoutsBalance(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
    );
    const { data: referralsBalance, isLoading: isReferralsBalanceLoading } = useSubscriberReferralsBalance(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
    );
    const { data: todayActivitySummary, isLoading: isTodayActivityLoading } = useSubscriberStatsTodayActivitySummary(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
    );
    const { data: profileVisitorsData, isLoading: isProfileVisitorsLoading } = useSubscriberStatsProfileVisitorsAll(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
        currentRangeQuery,
    );
    const { data: comparisonProfileVisitorsData } = useSubscriberStatsProfileVisitorsAll(
        subscriber.isLoggedIn && comparisonRangeQuery ? String(subscriber._id) : undefined,
        comparisonRangeQuery,
    );
    const performerTopValue = Number(performerTopData?.performerTop || 0);
    const lifetimeNetRevenue = useMemo(() => safeTotal(data?.total, 'net'), [data]);
    const comparisonLifetimeNetRevenue = useMemo(() => safeTotal(comparisonData?.total, 'net'), [comparisonData]);
    const chargebacksValue = useMemo(() => Number(chargebacksData?.total || 0), [chargebacksData]);
    const comparisonChargebacksValue = useMemo(() => Number(comparisonChargebacksData?.total || 0), [comparisonChargebacksData]);
    const ltvUsersBase = Number(usersCounters?.subscribers?.all || 0);
    const ltvRevenueBase = lifetimeNetRevenue - chargebacksValue;
    const comparisonLtvRevenueBase = comparisonLifetimeNetRevenue - comparisonChargebacksValue;
    const ltvValue = ltvUsersBase > 0 ? ltvRevenueBase / ltvUsersBase : 0;
    const comparisonLtvValue = ltvUsersBase > 0 ? comparisonLtvRevenueBase / ltvUsersBase : 0;
    const activeFansValue = Number(usersCounters?.subscribers?.active || 0);
    const activeFollowingValue = Number(usersCounters?.subscriptions?.active || 0);
    const payoutAvailableValue = Number(payoutsBalance?.payoutAvailable || 0);
    const payoutPendingValue = Number(payoutsBalance?.payoutPending || 0);
    const referralsBalanceValue = Number(referralsBalance?.referralEarnings || 0);
    const todayStoriesValue = Number(todayActivitySummary?.stories || 0);
    const todayPostsValue = Number(todayActivitySummary?.posts || 0);
    const todayChatsValue = Number(todayActivitySummary?.chats || 0);
    const todayStreamsValue = Number(todayActivitySummary?.streams || 0);
    const profileVisitorsTotalValue = safeNumber(profileVisitorsData?.topCountries?.totals?.total);
    const profileVisitorsGuestsValue = safeNumber(profileVisitorsData?.topCountries?.totals?.guests);
    const profileVisitorsUsersValue = safeNumber(profileVisitorsData?.topCountries?.totals?.users);
    const profileVisitorsSubscribersValue = safeNumber(profileVisitorsData?.topCountries?.totals?.subscribers);
    const comparisonProfileVisitorsTotalValue = safeNumber(comparisonProfileVisitorsData?.topCountries?.totals?.total);
    const comparisonProfileVisitorsGuestsValue = safeNumber(comparisonProfileVisitorsData?.topCountries?.totals?.guests);
    const comparisonProfileVisitorsUsersValue = safeNumber(comparisonProfileVisitorsData?.topCountries?.totals?.users);
    const comparisonProfileVisitorsSubscribersValue = safeNumber(comparisonProfileVisitorsData?.topCountries?.totals?.subscribers);
    const gmtTodayStartDate = useMemo(() => moment.utc().startOf('day').toDate(), []);
    const gmtTodayEndDate = useMemo(() => moment.utc().toDate(), []);
    const { data: newSubscribersTodayCount, isLoading: isNewSubscribersTodayCountLoading } = useSubscriberStatsNewSubscribersCount(
        subscriber.isLoggedIn ? String(subscriber._id) : undefined,
        gmtTodayStartDate,
        gmtTodayEndDate,
        'new',
    );
    const newSubscribersTodayValue = Number.isFinite(Number(newSubscribersTodayCount ?? 0)) ? Number(newSubscribersTodayCount ?? 0) : 0;
    const metrics = useMemo(() => extractMetrics(data, amountType), [data, amountType]);
    const deltas = useMemo(() => extractDeltas(data), [data]);
    const comparisonMetrics = useMemo(() => extractMetrics(comparisonData, amountType), [comparisonData, amountType]);

    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onEarningsDataLoaded(String(subscriber._id), data || null);
    }, [subscriber, data, onEarningsDataLoaded]);
    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onComparisonEarningsDataLoaded(String(subscriber._id), comparisonData || null);
    }, [subscriber, comparisonData, onComparisonEarningsDataLoaded]);
    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onChargebacksDataLoaded(String(subscriber._id), chargebacksData || null);
    }, [subscriber, chargebacksData, onChargebacksDataLoaded]);
    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onComparisonChargebacksDataLoaded(String(subscriber._id), comparisonChargebacksData || null);
    }, [subscriber, comparisonChargebacksData, onComparisonChargebacksDataLoaded]);
    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onProfileVisitorsDataLoaded(String(subscriber._id), profileVisitorsData || null);
    }, [subscriber, profileVisitorsData, onProfileVisitorsDataLoaded]);
    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        onComparisonProfileVisitorsDataLoaded(String(subscriber._id), comparisonProfileVisitorsData || null);
    }, [subscriber, comparisonProfileVisitorsData, onComparisonProfileVisitorsDataLoaded]);

    React.useEffect(() => {
        if (!subscriber?._id) {
            return;
        }
        if (!subscriber.isLoggedIn) {
            onLoaded(String(subscriber._id), zeroMetrics, extractDeltas(null), {
                earnings: true,
                chargebacks: true,
                newSubscribersToday: true,
                usersCounters: true,
                performerTop: true,
                payoutsBalance: true,
                referralsBalance: true,
                todayActivity: true,
                profileVisitors: true,
            });
            return;
        }
        onLoaded(
            String(subscriber._id),
            {
                ...metrics,
                chargebacks: chargebacksValue,
                ltv: ltvValue,
                ltvRevenueBase,
                ltvUsersBase,
                newSubscribersToday: Number.isFinite(Number(newSubscribersTodayCount ?? 0)) ? Number(newSubscribersTodayCount ?? 0) : 0,
                activeFans: activeFansValue,
                activeFollowing: activeFollowingValue,
                performerTop: performerTopValue,
                payoutAvailable: payoutAvailableValue,
                payoutPending: payoutPendingValue,
                referralsBalance: referralsBalanceValue,
                todayStories: todayStoriesValue,
                todayPosts: todayPostsValue,
                todayChats: todayChatsValue,
                todayStreams: todayStreamsValue,
                profileVisitorsTotal: profileVisitorsTotalValue,
                profileVisitorsGuests: profileVisitorsGuestsValue,
                profileVisitorsUsers: profileVisitorsUsersValue,
                profileVisitorsSubscribers: profileVisitorsSubscribersValue,
            },
            deltas,
            {
                earnings: !isLoading,
                chargebacks: !isChargebacksLoading,
                newSubscribersToday: !isNewSubscribersTodayCountLoading,
                usersCounters: !isUsersCountersLoading,
                performerTop: !isPerformerTopLoading,
                payoutsBalance: !isPayoutsBalanceLoading,
                referralsBalance: !isReferralsBalanceLoading,
                todayActivity: !isTodayActivityLoading,
                profileVisitors: !isProfileVisitorsLoading,
            },
        );
    }, [
        subscriber,
        metrics,
        chargebacksValue,
        deltas,
        ltvValue,
        ltvRevenueBase,
        ltvUsersBase,
        isLoading,
        isChargebacksLoading,
        usersCounters,
        isUsersCountersLoading,
        payoutAvailableValue,
        payoutPendingValue,
        isPayoutsBalanceLoading,
        referralsBalanceValue,
        isReferralsBalanceLoading,
        todayStoriesValue,
        todayPostsValue,
        todayChatsValue,
        todayStreamsValue,
        isTodayActivityLoading,
        profileVisitorsTotalValue,
        profileVisitorsGuestsValue,
        profileVisitorsUsersValue,
        profileVisitorsSubscribersValue,
        isProfileVisitorsLoading,
        performerTopData,
        isPerformerTopLoading,
        newSubscribersTodayCount,
        isNewSubscribersTodayCountLoading,
        onLoaded,
        onProfileVisitorsDataLoaded,
        onComparisonProfileVisitorsDataLoaded,
    ]);

    return (
        <Card>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'grey.100',
                    p: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography variant="subtitle2" sx={{ ml: 1 }}>
                    Include in Total
                </Typography>
                <Checkbox
                    checked={checked}
                    onChange={() => onToggleChecked(String(subscriber._id))}
                    inputProps={{ 'aria-label': `Select ${subscriber.username}` }}
                    sx={{ p: 0 }}
                />
            </Box>
            <Box sx={{ p: 1.5 }}>
                <Box sx={{ mb: 0.5 }}>
                    <UsernameWithAvatar
                        subscriber={subscriber}
                        category={subscriber?.cognito?.categoryDetails?.[0]}
                        refetchSubscribers={() => {}}
                        showLocalMenu={false}
                        enableLoginActions={false}
                        showCategoryMoveAction={false}
                    />
                </Box>
                <Grid container spacing={0.75} sx={{ mt: 0.25 }}>
                    <Grid item xs={6}>
                        <Typography variant="caption">Earnings</Typography>
                        {isLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{formatUsdMoney(metrics.earnings)}</Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={metrics.earnings - comparisonMetrics.earnings >= 0 ? 'success.main' : 'error.main'}
                                    >
                                        {metrics.earnings - comparisonMetrics.earnings >= 0 ? '↑' : '↓'}{' '}
                                        {formatUsdMoney(Math.abs(metrics.earnings - comparisonMetrics.earnings))}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Chargebacks</Typography>
                        {isChargebacksLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{formatUsdMoney(chargebacksValue)}</Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={chargebacksValue - comparisonChargebacksValue >= 0 ? 'success.main' : 'error.main'}
                                    >
                                        {chargebacksValue - comparisonChargebacksValue >= 0 ? '↑' : '↓'}{' '}
                                        {formatUsdMoney(Math.abs(chargebacksValue - comparisonChargebacksValue))}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">LTV</Typography>
                        {isLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{formatUsdMoney(ltvValue)}</Typography>
                                {showComparison ? (
                                    <Typography variant="body2" color={ltvValue - comparisonLtvValue >= 0 ? 'success.main' : 'error.main'}>
                                        {ltvValue - comparisonLtvValue >= 0 ? '↑' : '↓'}{' '}
                                        {formatUsdMoney(Math.abs(ltvValue - comparisonLtvValue))}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Tips</Typography>
                        {isLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{formatUsdMoney(metrics.tips)}</Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={metrics.tips - comparisonMetrics.tips >= 0 ? 'success.main' : 'error.main'}
                                    >
                                        {metrics.tips - comparisonMetrics.tips >= 0 ? '↑' : '↓'}{' '}
                                        {formatUsdMoney(Math.abs(metrics.tips - comparisonMetrics.tips))}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Messages</Typography>
                        {isLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{formatUsdMoney(metrics.messages)}</Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={metrics.messages - comparisonMetrics.messages >= 0 ? 'success.main' : 'error.main'}
                                    >
                                        {metrics.messages - comparisonMetrics.messages >= 0 ? '↑' : '↓'}{' '}
                                        {formatUsdMoney(Math.abs(metrics.messages - comparisonMetrics.messages))}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Posts</Typography>
                        {isLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{formatUsdMoney(metrics.posts)}</Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={metrics.posts - comparisonMetrics.posts >= 0 ? 'success.main' : 'error.main'}
                                    >
                                        {metrics.posts - comparisonMetrics.posts >= 0 ? '↑' : '↓'}{' '}
                                        {formatUsdMoney(Math.abs(metrics.posts - comparisonMetrics.posts))}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Subscriptions</Typography>
                        {isLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>
                                    {formatUsdMoney(metrics.subscriptions)}
                                </Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={metrics.subscriptions - comparisonMetrics.subscriptions >= 0 ? 'success.main' : 'error.main'}
                                    >
                                        {metrics.subscriptions - comparisonMetrics.subscriptions >= 0 ? '↑' : '↓'}{' '}
                                        {formatUsdMoney(Math.abs(metrics.subscriptions - comparisonMetrics.subscriptions))}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Stream Tips</Typography>
                        {isLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{formatUsdMoney(metrics.streamTips)}</Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={metrics.streamTips - comparisonMetrics.streamTips >= 0 ? 'success.main' : 'error.main'}
                                    >
                                        {metrics.streamTips - comparisonMetrics.streamTips >= 0 ? '↑' : '↓'}{' '}
                                        {formatUsdMoney(Math.abs(metrics.streamTips - comparisonMetrics.streamTips))}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Referral Earnings</Typography>
                        {isLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>
                                    {formatUsdMoney(metrics.referralEarnings)}
                                </Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={
                                            metrics.referralEarnings - comparisonMetrics.referralEarnings >= 0
                                                ? 'success.main'
                                                : 'error.main'
                                        }
                                    >
                                        {metrics.referralEarnings - comparisonMetrics.referralEarnings >= 0 ? '↑' : '↓'}{' '}
                                        {formatUsdMoney(Math.abs(metrics.referralEarnings - comparisonMetrics.referralEarnings))}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Subscribers (Today)</Typography>
                        {isNewSubscribersTodayCountLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>
                                    {newSubscribersTodayValue.toLocaleString()}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Fans</Typography>
                        {isUsersCountersLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{activeFansValue.toLocaleString()}</Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Following</Typography>
                        {isUsersCountersLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>
                                    {activeFollowingValue.toLocaleString()}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Performer Rank</Typography>
                        {isPerformerTopLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{performerTopValue.toFixed(2)}%</Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Payout Available</Typography>
                        {isPayoutsBalanceLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>
                                    {formatUsdMoney(payoutAvailableValue)}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Payout Pending</Typography>
                        {isPayoutsBalanceLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{formatUsdMoney(payoutPendingValue)}</Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Referrals Balance</Typography>
                        {isReferralsBalanceLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>
                                    {formatUsdMoney(referralsBalanceValue)}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Stories Today</Typography>
                        {isTodayActivityLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{todayStoriesValue.toLocaleString()}</Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Posts Today</Typography>
                        {isTodayActivityLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{todayPostsValue.toLocaleString()}</Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Chats Today</Typography>
                        {isTodayActivityLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{todayChatsValue.toLocaleString()}</Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Streams Today</Typography>
                        {isTodayActivityLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>{todayStreamsValue.toLocaleString()}</Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Profile Visitors</Typography>
                        {isProfileVisitorsLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>
                                    {profileVisitorsTotalValue.toLocaleString()}
                                </Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={
                                            profileVisitorsTotalValue - comparisonProfileVisitorsTotalValue >= 0
                                                ? 'success.main'
                                                : 'error.main'
                                        }
                                    >
                                        {profileVisitorsTotalValue - comparisonProfileVisitorsTotalValue >= 0 ? '↑' : '↓'}{' '}
                                        {Math.abs(profileVisitorsTotalValue - comparisonProfileVisitorsTotalValue).toLocaleString()}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Visitor Guests</Typography>
                        {isProfileVisitorsLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>
                                    {profileVisitorsGuestsValue.toLocaleString()}
                                </Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={
                                            profileVisitorsGuestsValue - comparisonProfileVisitorsGuestsValue >= 0
                                                ? 'success.main'
                                                : 'error.main'
                                        }
                                    >
                                        {profileVisitorsGuestsValue - comparisonProfileVisitorsGuestsValue >= 0 ? '↑' : '↓'}{' '}
                                        {Math.abs(profileVisitorsGuestsValue - comparisonProfileVisitorsGuestsValue).toLocaleString()}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Visitor Users</Typography>
                        {isProfileVisitorsLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>
                                    {profileVisitorsUsersValue.toLocaleString()}
                                </Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={
                                            profileVisitorsUsersValue - comparisonProfileVisitorsUsersValue >= 0
                                                ? 'success.main'
                                                : 'error.main'
                                        }
                                    >
                                        {profileVisitorsUsersValue - comparisonProfileVisitorsUsersValue >= 0 ? '↑' : '↓'}{' '}
                                        {Math.abs(profileVisitorsUsersValue - comparisonProfileVisitorsUsersValue).toLocaleString()}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption">Visitor Subscribers</Typography>
                        {isProfileVisitorsLoading ? (
                            <Typography>...</Typography>
                        ) : (
                            <Box>
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 600 }}>
                                    {profileVisitorsSubscribersValue.toLocaleString()}
                                </Typography>
                                {showComparison ? (
                                    <Typography
                                        variant="body2"
                                        color={
                                            profileVisitorsSubscribersValue - comparisonProfileVisitorsSubscribersValue >= 0
                                                ? 'success.main'
                                                : 'error.main'
                                        }
                                    >
                                        {profileVisitorsSubscribersValue - comparisonProfileVisitorsSubscribersValue >= 0 ? '↑' : '↓'}{' '}
                                        {Math.abs(
                                            profileVisitorsSubscribersValue - comparisonProfileVisitorsSubscribersValue,
                                        ).toLocaleString()}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Box>
        </Card>
    );
};

const DashboardAnalytics = () => {
    const { data: subscribers, isLoading } = useSubscribers();
    const theme = useTheme();
    const analyticsTableSurface = useMemo(
        () => ({
            headerBg: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
            rowHoverBg:
                theme.palette.mode === 'dark'
                    ? lighten(theme.palette.background.paper, 0.06)
                    : darken(theme.palette.background.paper, 0.04),
        }),
        [theme],
    );
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const isDesktopRangePicker = useMediaQuery(theme.breakpoints.up('md'));
    const [filter, setFilter] = useState('');
    const [sortBy, setSortBy] = useState('username');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [amountType, setAmountType] = useState<AnalyticsAmountType>(() => {
        if (typeof window === 'undefined') {
            return 'net';
        }
        return localStorage.getItem('showEarningsAsGross') === 'true' ? 'gross' : 'net';
    });
    const [rangePreset, setRangePreset] = useState<AnalyticsRangePreset>('all');
    const [customRangeStartDate, setCustomRangeStartDate] = useState<Date | null>(null);
    const [customRangeEndDate, setCustomRangeEndDate] = useState<Date | null>(null);
    const [isCustomRangeDialogOpen, setIsCustomRangeDialogOpen] = useState(false);
    const [draftCustomRange, setDraftCustomRange] = useState<DateRange<Moment>>([null, null]);
    const [comparisonPreset, setComparisonPreset] = useState<AnalyticsComparisonPreset>('previousPeriod');
    useEffect(() => {
        if (rangePreset === 'all' && comparisonPreset !== 'none') {
            setComparisonPreset('none');
        }
    }, [rangePreset, comparisonPreset]);
    const activeMainRangeLabel = useMemo(() => {
        if (rangePreset === 'all') {
            return 'All time';
        }
        if (rangePreset === 'custom') {
            return formatRangeLabel(customRangeStartDate, customRangeEndDate);
        }
        const start = getRangeStartDate(rangePreset);
        const end = moment.utc().endOf('day').toDate();
        return formatRangeLabel(start || null, end);
    }, [rangePreset, customRangeStartDate, customRangeEndDate]);
    const openCustomRangeDialog = useCallback(() => {
        setDraftCustomRange([
            customRangeStartDate ? moment.utc(customRangeStartDate) : null,
            customRangeEndDate ? moment.utc(customRangeEndDate) : null,
        ]);
        setIsCustomRangeDialogOpen(true);
    }, [customRangeStartDate, customRangeEndDate]);
    const applyCustomRange = useCallback(() => {
        const [start, end] = draftCustomRange;
        if (!start || !end) {
            return;
        }
        setCustomRangeStartDate(start.clone().startOf('day').toDate());
        setCustomRangeEndDate(end.clone().endOf('day').toDate());
        setRangePreset('custom');
        setIsCustomRangeDialogOpen(false);
    }, [draftCustomRange]);
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        localStorage.setItem('showEarningsAsGross', amountType === 'gross' ? 'true' : 'false');
    }, [amountType]);

    const [groupByCategory, setGroupByCategory] = useState(true);
    const [selectedChartMetric, setSelectedChartMetric] = useState<AnalyticsChartMetricKey>('earnings');
    const [metricsBySubscriberId, setMetricsBySubscriberId] = useState<Record<string, AnalyticsMetrics>>({});
    const [deltasBySubscriberId, setDeltasBySubscriberId] = useState<Record<string, AnalyticsDeltas>>({});
    const [earningsDataBySubscriberId, setEarningsDataBySubscriberId] = useState<
        Record<string, OnlyFansStatisticsEarningsOverviewAndChartData.Response | null>
    >({});
    const [comparisonEarningsDataBySubscriberId, setComparisonEarningsDataBySubscriberId] = useState<
        Record<string, OnlyFansStatisticsEarningsOverviewAndChartData.Response | null>
    >({});
    const [chargebacksDataBySubscriberId, setChargebacksDataBySubscriberId] = useState<
        Record<string, OnlyFansStatisticsChargebacksOverviewAndChartData.Response | null>
    >({});
    const [comparisonChargebacksDataBySubscriberId, setComparisonChargebacksDataBySubscriberId] = useState<
        Record<string, OnlyFansStatisticsChargebacksOverviewAndChartData.Response | null>
    >({});
    const [profileVisitorsDataBySubscriberId, setProfileVisitorsDataBySubscriberId] = useState<
        Record<string, OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration.Response | null>
    >({});
    const [comparisonProfileVisitorsDataBySubscriberId, setComparisonProfileVisitorsDataBySubscriberId] = useState<
        Record<string, OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration.Response | null>
    >({});
    const [earningsLoadedBySubscriberId, setEarningsLoadedBySubscriberId] = useState<Record<string, boolean>>({});
    const [chargebacksLoadedBySubscriberId, setChargebacksLoadedBySubscriberId] = useState<Record<string, boolean>>({});
    const [newSubscribersLoadedBySubscriberId, setNewSubscribersLoadedBySubscriberId] = useState<Record<string, boolean>>({});
    const [usersCountersLoadedBySubscriberId, setUsersCountersLoadedBySubscriberId] = useState<Record<string, boolean>>({});
    const [performerTopLoadedBySubscriberId, setPerformerTopLoadedBySubscriberId] = useState<Record<string, boolean>>({});
    const [payoutsBalanceLoadedBySubscriberId, setPayoutsBalanceLoadedBySubscriberId] = useState<Record<string, boolean>>({});
    const [referralsBalanceLoadedBySubscriberId, setReferralsBalanceLoadedBySubscriberId] = useState<Record<string, boolean>>({});
    const [todayActivityLoadedBySubscriberId, setTodayActivityLoadedBySubscriberId] = useState<Record<string, boolean>>({});
    const [profileVisitorsLoadedBySubscriberId, setProfileVisitorsLoadedBySubscriberId] = useState<Record<string, boolean>>({});
    const [selectedBySubscriberId, setSelectedBySubscriberId] = useState<Record<string, boolean>>({});

    const onLoaded = useCallback(
        (
            subscriberId: string,
            metrics: AnalyticsMetrics,
            deltas: AnalyticsDeltas,
            loaded: {
                earnings: boolean;
                chargebacks: boolean;
                newSubscribersToday: boolean;
                usersCounters: boolean;
                performerTop: boolean;
                payoutsBalance: boolean;
                referralsBalance: boolean;
                todayActivity: boolean;
                profileVisitors: boolean;
            },
        ) => {
            setMetricsBySubscriberId(prev => ({ ...prev, [subscriberId]: metrics }));
            setDeltasBySubscriberId(prev => ({ ...prev, [subscriberId]: deltas }));
            setEarningsLoadedBySubscriberId(prev => ({ ...prev, [subscriberId]: loaded.earnings }));
            setChargebacksLoadedBySubscriberId(prev => ({ ...prev, [subscriberId]: loaded.chargebacks }));
            setNewSubscribersLoadedBySubscriberId(prev => ({ ...prev, [subscriberId]: loaded.newSubscribersToday }));
            setUsersCountersLoadedBySubscriberId(prev => ({ ...prev, [subscriberId]: loaded.usersCounters }));
            setPerformerTopLoadedBySubscriberId(prev => ({ ...prev, [subscriberId]: loaded.performerTop }));
            setPayoutsBalanceLoadedBySubscriberId(prev => ({ ...prev, [subscriberId]: loaded.payoutsBalance }));
            setReferralsBalanceLoadedBySubscriberId(prev => ({ ...prev, [subscriberId]: loaded.referralsBalance }));
            setTodayActivityLoadedBySubscriberId(prev => ({ ...prev, [subscriberId]: loaded.todayActivity }));
            setProfileVisitorsLoadedBySubscriberId(prev => ({ ...prev, [subscriberId]: loaded.profileVisitors }));
        },
        [],
    );

    const onEarningsDataLoaded = useCallback(
        (subscriberId: string, data: OnlyFansStatisticsEarningsOverviewAndChartData.Response | null) => {
            setEarningsDataBySubscriberId(prev => ({ ...prev, [subscriberId]: data }));
        },
        [],
    );
    const onComparisonEarningsDataLoaded = useCallback(
        (subscriberId: string, data: OnlyFansStatisticsEarningsOverviewAndChartData.Response | null) => {
            setComparisonEarningsDataBySubscriberId(prev => ({ ...prev, [subscriberId]: data }));
        },
        [],
    );
    const onChargebacksDataLoaded = useCallback(
        (subscriberId: string, data: OnlyFansStatisticsChargebacksOverviewAndChartData.Response | null) => {
            setChargebacksDataBySubscriberId(prev => ({ ...prev, [subscriberId]: data }));
        },
        [],
    );
    const onComparisonChargebacksDataLoaded = useCallback(
        (subscriberId: string, data: OnlyFansStatisticsChargebacksOverviewAndChartData.Response | null) => {
            setComparisonChargebacksDataBySubscriberId(prev => ({ ...prev, [subscriberId]: data }));
        },
        [],
    );
    const onProfileVisitorsDataLoaded = useCallback(
        (subscriberId: string, data: OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration.Response | null) => {
            setProfileVisitorsDataBySubscriberId(prev => ({ ...prev, [subscriberId]: data }));
        },
        [],
    );
    const onComparisonProfileVisitorsDataLoaded = useCallback(
        (subscriberId: string, data: OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration.Response | null) => {
            setComparisonProfileVisitorsDataBySubscriberId(prev => ({ ...prev, [subscriberId]: data }));
        },
        [],
    );

    useEffect(() => {
        if (!subscribers || !Array.isArray(subscribers)) {
            return;
        }

        setSelectedBySubscriberId(prev => {
            const next = { ...prev };
            subscribers.forEach(subscriber => {
                const id = String(subscriber._id);
                if (!(id in next)) {
                    next[id] = true;
                }
            });
            return next;
        });
    }, [subscribers]);

    const onToggleSelected = useCallback((subscriberId: string) => {
        setSelectedBySubscriberId(prev => ({
            ...prev,
            [subscriberId]: !prev[subscriberId],
        }));
    }, []);

    const onSetSelectedForGroup = useCallback((subscribersInGroup: OnlyFansSubscriber[], checked: boolean) => {
        setSelectedBySubscriberId(prev => {
            const next = { ...prev };
            subscribersInGroup.forEach(subscriber => {
                next[String(subscriber._id)] = checked;
            });
            return next;
        });
    }, []);

    const groupedData = useMemo(() => {
        const uniqueCategories: OnlyFansSubscriberCategory[] = [];
        const categoryMap = new Map<string, OnlyFansSubscriberCategory>();
        const groupedSubscribers: Record<string, OnlyFansSubscriber[]> = {};

        if (!subscribers || !Array.isArray(subscribers)) {
            return { uniqueCategories, groupedSubscribers, uncategorized: [] };
        }

        (subscribers || []).forEach((subscriber: any) => {
            const category = subscriber?.cognito?.categoryDetails?.[0];
            if (category?._id) {
                if (!categoryMap.has(category._id)) {
                    categoryMap.set(category._id, category);
                    uniqueCategories.push(category);
                }
                if (!groupedSubscribers[category._id]) {
                    groupedSubscribers[category._id] = [];
                }
                groupedSubscribers[category._id].push(subscriber);
            }
        });

        const uncategorized = (subscribers || []).filter((subscriber: any) => !subscriber?.cognito?.categoryDetails?.[0]?._id);
        return { uniqueCategories, groupedSubscribers, uncategorized };
    }, [subscribers]);

    const sortedUncategorized = useMemo(
        () => sortSubscribers(groupedData.uncategorized, metricsBySubscriberId, zeroMetrics, filter, sortBy, sortDirection),
        [groupedData.uncategorized, metricsBySubscriberId, filter, sortBy, sortDirection],
    );

    const sortedByCategoryMap = useMemo(() => {
        const map: Record<string, OnlyFansSubscriber[]> = {};
        groupedData.uniqueCategories.forEach(category => {
            map[category._id!] = sortSubscribers(
                groupedData.groupedSubscribers[category._id!] || [],
                metricsBySubscriberId,
                zeroMetrics,
                filter,
                sortBy,
                sortDirection,
            );
        });
        return map;
    }, [groupedData, metricsBySubscriberId, filter, sortBy, sortDirection]);

    const sortedAll = useMemo(
        () =>
            sortSubscribers((subscribers || []) as OnlyFansSubscriber[], metricsBySubscriberId, zeroMetrics, filter, sortBy, sortDirection),
        [subscribers, metricsBySubscriberId, filter, sortBy, sortDirection],
    );

    const visibleSubscribers = useMemo(() => {
        if (groupByCategory) {
            return [...sortedUncategorized, ...groupedData.uniqueCategories.flatMap(category => sortedByCategoryMap[category._id!] || [])];
        }
        return sortedAll;
    }, [groupByCategory, sortedUncategorized, groupedData.uniqueCategories, sortedByCategoryMap, sortedAll]);

    const selectedVisibleSubscribers = useMemo(
        () => visibleSubscribers.filter(subscriber => selectedBySubscriberId[String(subscriber._id)] !== false),
        [visibleSubscribers, selectedBySubscriberId],
    );
    const loggedInVisibleIds = useMemo(
        () => selectedVisibleSubscribers.filter(s => s.isLoggedIn && s._id).map(s => String(s._id)),
        [selectedVisibleSubscribers],
    );

    const totals = useMemo(() => {
        return selectedVisibleSubscribers.reduce(
            (acc, subscriber) => {
                const m = metricsBySubscriberId[String(subscriber._id)] || zeroMetrics;
                return {
                    earnings: acc.earnings + m.earnings,
                    chargebacks: acc.chargebacks + m.chargebacks,
                    ltv: acc.ltv + m.ltv,
                    ltvRevenueBase: acc.ltvRevenueBase + m.ltvRevenueBase,
                    ltvUsersBase: acc.ltvUsersBase + m.ltvUsersBase,
                    tips: acc.tips + m.tips,
                    referralEarnings: acc.referralEarnings + m.referralEarnings,
                    messages: acc.messages + m.messages,
                    posts: acc.posts + m.posts,
                    subscriptions: acc.subscriptions + m.subscriptions,
                    streamTips: acc.streamTips + m.streamTips,
                    newSubscribersToday: acc.newSubscribersToday + m.newSubscribersToday,
                    activeFans: acc.activeFans + m.activeFans,
                    activeFollowing: acc.activeFollowing + m.activeFollowing,
                    performerTop: acc.performerTop + m.performerTop,
                    payoutAvailable: acc.payoutAvailable + m.payoutAvailable,
                    payoutPending: acc.payoutPending + m.payoutPending,
                    referralsBalance: acc.referralsBalance + m.referralsBalance,
                    todayStories: acc.todayStories + m.todayStories,
                    todayPosts: acc.todayPosts + m.todayPosts,
                    todayChats: acc.todayChats + m.todayChats,
                    todayStreams: acc.todayStreams + m.todayStreams,
                    profileVisitorsTotal: acc.profileVisitorsTotal + m.profileVisitorsTotal,
                    profileVisitorsGuests: acc.profileVisitorsGuests + m.profileVisitorsGuests,
                    profileVisitorsUsers: acc.profileVisitorsUsers + m.profileVisitorsUsers,
                    profileVisitorsSubscribers: acc.profileVisitorsSubscribers + m.profileVisitorsSubscribers,
                };
            },
            { ...zeroMetrics },
        );
    }, [selectedVisibleSubscribers, metricsBySubscriberId]);

    const totalDeltas = useMemo(() => {
        return selectedVisibleSubscribers.reduce((acc, subscriber) => {
            const d = deltasBySubscriberId[String(subscriber._id)] || extractDeltas(null);
            return {
                earnings: acc.earnings + d.earnings,
                referralEarnings: acc.referralEarnings + d.referralEarnings,
                tips: acc.tips + d.tips,
                messages: acc.messages + d.messages,
                posts: acc.posts + d.posts,
                subscriptions: acc.subscriptions + d.subscriptions,
                streamTips: acc.streamTips + d.streamTips,
            };
        }, extractDeltas(null));
    }, [selectedVisibleSubscribers, deltasBySubscriberId]);

    const earningsLoadedCount = useMemo(
        () => loggedInVisibleIds.filter(id => earningsLoadedBySubscriberId[id]).length,
        [loggedInVisibleIds, earningsLoadedBySubscriberId],
    );
    const chargebacksLoadedCount = useMemo(
        () => loggedInVisibleIds.filter(id => chargebacksLoadedBySubscriberId[id]).length,
        [loggedInVisibleIds, chargebacksLoadedBySubscriberId],
    );
    const newSubscribersLoadedCount = useMemo(
        () => loggedInVisibleIds.filter(id => newSubscribersLoadedBySubscriberId[id]).length,
        [loggedInVisibleIds, newSubscribersLoadedBySubscriberId],
    );
    const usersCountersLoadedCount = useMemo(
        () => loggedInVisibleIds.filter(id => usersCountersLoadedBySubscriberId[id]).length,
        [loggedInVisibleIds, usersCountersLoadedBySubscriberId],
    );
    const performerTopLoadedCount = useMemo(
        () => loggedInVisibleIds.filter(id => performerTopLoadedBySubscriberId[id]).length,
        [loggedInVisibleIds, performerTopLoadedBySubscriberId],
    );
    const payoutsBalanceLoadedCount = useMemo(
        () => loggedInVisibleIds.filter(id => payoutsBalanceLoadedBySubscriberId[id]).length,
        [loggedInVisibleIds, payoutsBalanceLoadedBySubscriberId],
    );
    const referralsBalanceLoadedCount = useMemo(
        () => loggedInVisibleIds.filter(id => referralsBalanceLoadedBySubscriberId[id]).length,
        [loggedInVisibleIds, referralsBalanceLoadedBySubscriberId],
    );
    const todayActivityLoadedCount = useMemo(
        () => loggedInVisibleIds.filter(id => todayActivityLoadedBySubscriberId[id]).length,
        [loggedInVisibleIds, todayActivityLoadedBySubscriberId],
    );
    const profileVisitorsLoadedCount = useMemo(
        () => loggedInVisibleIds.filter(id => profileVisitorsLoadedBySubscriberId[id]).length,
        [loggedInVisibleIds, profileVisitorsLoadedBySubscriberId],
    );
    const averagePerformerTop = useMemo(() => {
        if (loggedInVisibleIds.length === 0) {
            return 0;
        }
        return totals.performerTop / loggedInVisibleIds.length;
    }, [totals.performerTop, loggedInVisibleIds.length]);
    const maxCount = loggedInVisibleIds.length;
    const isReportFullyLoaded = useMemo(() => {
        if (maxCount === 0) {
            return true;
        }
        return (
            earningsLoadedCount >= maxCount &&
            chargebacksLoadedCount >= maxCount &&
            newSubscribersLoadedCount >= maxCount &&
            usersCountersLoadedCount >= maxCount &&
            performerTopLoadedCount >= maxCount &&
            payoutsBalanceLoadedCount >= maxCount &&
            referralsBalanceLoadedCount >= maxCount &&
            todayActivityLoadedCount >= maxCount &&
            profileVisitorsLoadedCount >= maxCount
        );
    }, [
        maxCount,
        earningsLoadedCount,
        chargebacksLoadedCount,
        newSubscribersLoadedCount,
        usersCountersLoadedCount,
        performerTopLoadedCount,
        payoutsBalanceLoadedCount,
        referralsBalanceLoadedCount,
        todayActivityLoadedCount,
        profileVisitorsLoadedCount,
    ]);

    const display = {
        earnings: useAnimatedSummaryNumber(totals.earnings),
        chargebacks: useAnimatedSummaryNumber(totals.chargebacks),
        referralEarnings: useAnimatedSummaryNumber(totals.referralEarnings),
        ltv: useAnimatedSummaryNumber(totals.ltvUsersBase > 0 ? totals.ltvRevenueBase / totals.ltvUsersBase : 0),
        tips: useAnimatedSummaryNumber(totals.tips),
        messages: useAnimatedSummaryNumber(totals.messages),
        posts: useAnimatedSummaryNumber(totals.posts),
        subscriptions: useAnimatedSummaryNumber(totals.subscriptions),
        streamTips: useAnimatedSummaryNumber(totals.streamTips),
        newSubscribersToday: useAnimatedSummaryNumber(totals.newSubscribersToday),
        activeFans: useAnimatedSummaryNumber(totals.activeFans),
        activeFollowing: useAnimatedSummaryNumber(totals.activeFollowing),
        payoutAvailable: useAnimatedSummaryNumber(totals.payoutAvailable),
        payoutPending: useAnimatedSummaryNumber(totals.payoutPending),
        referralsBalance: useAnimatedSummaryNumber(totals.referralsBalance),
        todayStories: useAnimatedSummaryNumber(totals.todayStories),
        todayPosts: useAnimatedSummaryNumber(totals.todayPosts),
        todayChats: useAnimatedSummaryNumber(totals.todayChats),
        todayStreams: useAnimatedSummaryNumber(totals.todayStreams),
        profileVisitorsTotal: useAnimatedSummaryNumber(totals.profileVisitorsTotal),
        profileVisitorsGuests: useAnimatedSummaryNumber(totals.profileVisitorsGuests),
        profileVisitorsUsers: useAnimatedSummaryNumber(totals.profileVisitorsUsers),
        profileVisitorsSubscribers: useAnimatedSummaryNumber(totals.profileVisitorsSubscribers),
    };
    const comparisonDisplay = {
        earnings: totals.earnings - totalDeltas.earnings,
        chargebacks: selectedVisibleSubscribers.reduce((acc, subscriber) => {
            const d = comparisonChargebacksDataBySubscriberId[String(subscriber._id)];
            return acc + Number(d?.total || 0);
        }, 0),
        referralEarnings: totals.referralEarnings - totalDeltas.referralEarnings,
        tips: totals.tips - totalDeltas.tips,
        messages: totals.messages - totalDeltas.messages,
        posts: totals.posts - totalDeltas.posts,
        subscriptions: totals.subscriptions - totalDeltas.subscriptions,
        streamTips: totals.streamTips - totalDeltas.streamTips,
    };
    const comparisonProfileVisitorsTotals = useMemo(
        () =>
            selectedVisibleSubscribers.reduce(
                (acc, subscriber) => {
                    const comparisonData = comparisonProfileVisitorsDataBySubscriberId[String(subscriber._id)];
                    acc.total += safeNumber(comparisonData?.topCountries?.totals?.total);
                    acc.guests += safeNumber(comparisonData?.topCountries?.totals?.guests);
                    acc.users += safeNumber(comparisonData?.topCountries?.totals?.users);
                    acc.subscribers += safeNumber(comparisonData?.topCountries?.totals?.subscribers);
                    return acc;
                },
                { total: 0, guests: 0, users: 0, subscribers: 0 },
            ),
        [selectedVisibleSubscribers, comparisonProfileVisitorsDataBySubscriberId],
    );
    const profileVisitorsCountriesSummary = useMemo(() => {
        const byCountry = new Map<
            string,
            { countryName: string; countryCode: string; total: number; guests: number; users: number; subscribers: number }
        >();
        selectedVisibleSubscribers.forEach(subscriber => {
            const data = profileVisitorsDataBySubscriberId[String(subscriber._id)];
            (data?.topCountries?.rows || []).forEach(row => {
                const key = row.countryCode || row.countryName || 'unknown';
                const existing = byCountry.get(key) || {
                    countryName: row.countryName || 'Unknown',
                    countryCode: row.countryCode || '',
                    total: 0,
                    guests: 0,
                    users: 0,
                    subscribers: 0,
                };
                existing.total += safeNumber(row.viewsCount?.total);
                existing.guests += safeNumber(row.viewsCount?.guests);
                existing.users += safeNumber(row.viewsCount?.users);
                existing.subscribers += safeNumber(row.viewsCount?.subscribers);
                byCountry.set(key, existing);
            });
        });
        return Array.from(byCountry.values()).sort((a, b) => b.total - a.total);
    }, [selectedVisibleSubscribers, profileVisitorsDataBySubscriberId]);
    const profileVisitorsCountriesTotal = useMemo(
        () => profileVisitorsCountriesSummary.reduce((acc, row) => acc + safeNumber(row.total), 0),
        [profileVisitorsCountriesSummary],
    );
    const profileVisitorsChartData = useMemo(() => {
        const totalsByDate = new Map<string, number>();
        selectedVisibleSubscribers.forEach(subscriber => {
            const data = profileVisitorsDataBySubscriberId[String(subscriber._id)];
            (data?.chart?.visitors || []).forEach(point => {
                const dayKey = moment.utc(point.date).format('YYYY-MM-DD');
                const value = safeNumber(point.count);
                totalsByDate.set(dayKey, (totalsByDate.get(dayKey) || 0) + value);
            });
        });
        return Array.from(totalsByDate.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([day, value]) => ({
                time: moment.utc(day, 'YYYY-MM-DD').valueOf(),
                value,
            }));
    }, [selectedVisibleSubscribers, profileVisitorsDataBySubscriberId]);
    const profileVisitorsComparisonChartData = useMemo(() => {
        if (comparisonPreset !== 'previousPeriod' || rangePreset === 'all' || profileVisitorsChartData.length === 0) {
            return [];
        }
        const totalsByDate = new Map<string, number>();
        selectedVisibleSubscribers.forEach(subscriber => {
            const data = comparisonProfileVisitorsDataBySubscriberId[String(subscriber._id)];
            (data?.chart?.visitors || []).forEach(point => {
                const dayKey = moment.utc(point.date).format('YYYY-MM-DD');
                const value = safeNumber(point.count);
                totalsByDate.set(dayKey, (totalsByDate.get(dayKey) || 0) + value);
            });
        });
        const comparisonPoints = Array.from(totalsByDate.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([day, value]) => ({ time: moment.utc(day, 'YYYY-MM-DD').valueOf(), value }));
        if (comparisonPoints.length === 0) {
            return [];
        }
        const shiftMs = profileVisitorsChartData[0].time - comparisonPoints[0].time;
        return comparisonPoints.map(point => ({
            time: point.time + shiftMs,
            comparisonValue: point.value,
        }));
    }, [comparisonPreset, rangePreset, profileVisitorsChartData, selectedVisibleSubscribers, comparisonProfileVisitorsDataBySubscriberId]);
    const profileVisitorsCurrentLegendDateRange = useMemo(() => {
        if (profileVisitorsChartData.length === 0) {
            return null;
        }
        const t0 = profileVisitorsChartData[0].time;
        const t1 = profileVisitorsChartData[profileVisitorsChartData.length - 1].time;
        return `${moment.utc(t0).format('MMM D, YYYY')} - ${moment.utc(t1).format('MMM D, YYYY')}`;
    }, [profileVisitorsChartData]);
    const profileVisitorsComparisonLegendDateRange = useMemo(() => {
        if (comparisonPreset !== 'previousPeriod' || rangePreset === 'all' || profileVisitorsChartData.length === 0) {
            return null;
        }
        const currentStart = moment.utc(profileVisitorsChartData[0].time);
        const currentEnd = moment.utc(profileVisitorsChartData[profileVisitorsChartData.length - 1].time);
        const days = Math.max(1, currentEnd.diff(currentStart, 'days') + 1);
        const previousEnd = currentStart.clone().subtract(1, 'day').endOf('day');
        const previousStart = previousEnd
            .clone()
            .subtract(days - 1, 'days')
            .startOf('day');
        return `${previousStart.format('MMM D, YYYY')} - ${previousEnd.format('MMM D, YYYY')}`;
    }, [comparisonPreset, rangePreset, profileVisitorsChartData]);
    const profileVisitorsYAxisDomain = useMemo<[number, number]>(() => {
        const values: number[] = [];
        profileVisitorsChartData.forEach(point => values.push(safeNumber(point.value)));
        profileVisitorsComparisonChartData.forEach(point => values.push(safeNumber(point.comparisonValue)));
        const maxValue = values.length > 0 ? Math.max(...values, 0) : 0;
        const paddedMax = maxValue > 0 ? maxValue * 1.1 : 100;
        return [0, paddedMax];
    }, [profileVisitorsChartData, profileVisitorsComparisonChartData]);
    const profileVisitorsPeakLowStat = useMemo(() => {
        if (profileVisitorsChartData.length === 0) {
            return null;
        }
        let maxPt = profileVisitorsChartData[0];
        let minPt = profileVisitorsChartData[0];
        profileVisitorsChartData.forEach(point => {
            if (point.value > maxPt.value) {
                maxPt = point;
            }
            if (point.value < minPt.value) {
                minPt = point;
            }
        });
        return { maxPt, minPt };
    }, [profileVisitorsChartData]);
    const profileVisitorsPeriodChangePercent = useMemo(() => {
        if (comparisonPreset !== 'previousPeriod' || rangePreset === 'all') {
            return null;
        }
        const cur = display.profileVisitorsTotal;
        const prev = comparisonProfileVisitorsTotals.total;
        if (!Number.isFinite(cur) || !Number.isFinite(prev) || prev === 0) {
            return null;
        }
        return ((cur - prev) / prev) * 100;
    }, [comparisonPreset, rangePreset, display.profileVisitorsTotal, comparisonProfileVisitorsTotals.total]);
    const showComparison = comparisonPreset === 'previousPeriod' && rangePreset !== 'all';
    const comparisonLtvRevenueBase = useMemo(() => {
        return selectedVisibleSubscribers.reduce((acc, subscriber) => {
            const comparisonData = comparisonEarningsDataBySubscriberId[String(subscriber._id)];
            const comparisonChargebacksData = comparisonChargebacksDataBySubscriberId[String(subscriber._id)];
            return acc + safeTotal(comparisonData?.total, 'net') - Number(comparisonChargebacksData?.total || 0);
        }, 0);
    }, [selectedVisibleSubscribers, comparisonEarningsDataBySubscriberId, comparisonChargebacksDataBySubscriberId]);
    const comparisonLtv = useMemo(
        () => (totals.ltvUsersBase > 0 ? comparisonLtvRevenueBase / totals.ltvUsersBase : 0),
        [comparisonLtvRevenueBase, totals.ltvUsersBase],
    );

    const selectedMetricChartData = useMemo(() => {
        const totalsByDate = new Map<string, number>();
        const isChargebacksMetric = selectedChartMetric === 'chargebacks';

        selectedVisibleSubscribers.forEach(subscriber => {
            if (!subscriber.isLoggedIn || !subscriber._id) {
                return;
            }

            const points = isChargebacksMetric
                ? chargebacksDataBySubscriberId[String(subscriber._id)]?.chartAmount ?? []
                : (() => {
                      const data = earningsDataBySubscriberId[String(subscriber._id)];
                      const dataKey = chartMetricToDataKey[selectedChartMetric];
                      if (!data || !dataKey) {
                          return [];
                      }

                      return data[dataKey]?.chartAmount || [];
                  })();
            points.forEach(point => {
                const dayKey = moment.utc(point.date).format('YYYY-MM-DD');
                const numeric = Number(point.count ?? 0);
                const value = Number.isFinite(numeric) ? numeric : 0;
                totalsByDate.set(dayKey, (totalsByDate.get(dayKey) || 0) + value);
            });
        });

        return Array.from(totalsByDate.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([day, value]) => ({
                time: moment.utc(day, 'YYYY-MM-DD').valueOf(),
                value,
            }));
    }, [selectedVisibleSubscribers, earningsDataBySubscriberId, chargebacksDataBySubscriberId, selectedChartMetric]);
    const comparisonMetricChartData = useMemo(() => {
        if (comparisonPreset !== 'previousPeriod' || rangePreset === 'all' || selectedMetricChartData.length === 0) {
            return [];
        }
        const totalsByDate = new Map<string, number>();
        const isChargebacksMetric = selectedChartMetric === 'chargebacks';
        selectedVisibleSubscribers.forEach(subscriber => {
            if (!subscriber.isLoggedIn || !subscriber._id) {
                return;
            }
            const points = isChargebacksMetric
                ? comparisonChargebacksDataBySubscriberId[String(subscriber._id)]?.chartAmount ?? []
                : (() => {
                      const data = comparisonEarningsDataBySubscriberId[String(subscriber._id)];
                      const dataKey = chartMetricToDataKey[selectedChartMetric];
                      if (!data || !dataKey) {
                          return [];
                      }

                      return data[dataKey]?.chartAmount || [];
                  })();
            points.forEach(point => {
                const dayKey = moment.utc(point.date).format('YYYY-MM-DD');
                const numeric = Number(point.count ?? 0);
                const value = Number.isFinite(numeric) ? numeric : 0;
                totalsByDate.set(dayKey, (totalsByDate.get(dayKey) || 0) + value);
            });
        });
        const comparisonPoints = Array.from(totalsByDate.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([day, value]) => ({ time: moment.utc(day, 'YYYY-MM-DD').valueOf(), value }));
        if (comparisonPoints.length === 0) {
            return [];
        }
        const shiftMs = selectedMetricChartData[0].time - comparisonPoints[0].time;
        return comparisonPoints.map(point => ({
            time: point.time + shiftMs,
            comparisonValue: point.value,
        }));
    }, [
        comparisonPreset,
        rangePreset,
        selectedMetricChartData,
        selectedVisibleSubscribers,
        comparisonEarningsDataBySubscriberId,
        comparisonChargebacksDataBySubscriberId,
        selectedChartMetric,
    ]);
    const yAxisDomain = useMemo<[number, number]>(() => {
        const values: number[] = [];
        selectedMetricChartData.forEach(point => values.push(Number(point.value || 0)));
        comparisonMetricChartData.forEach(point => values.push(Number(point.comparisonValue || 0)));
        const maxValue = values.length > 0 ? Math.max(...values, 0) : 0;
        const paddedMax = maxValue > 0 ? maxValue * 1.1 : 100;
        return [0, paddedMax];
    }, [selectedMetricChartData, comparisonMetricChartData]);

    const currentRangeStartDate = useMemo(() => {
        if (rangePreset === 'custom') {
            return customRangeStartDate || undefined;
        }
        return getRangeStartDate(rangePreset);
    }, [rangePreset, customRangeStartDate]);
    const currentRangeEndDate = useMemo(() => {
        if (rangePreset === 'custom') {
            return customRangeEndDate || undefined;
        }
        return moment.utc().endOf('day').toDate();
    }, [rangePreset, customRangeEndDate]);
    const previousPeriodRange = useMemo(() => {
        if (!showComparison || !currentRangeStartDate || !currentRangeEndDate) {
            return null;
        }
        const days = Math.max(1, moment.utc(currentRangeEndDate).diff(moment.utc(currentRangeStartDate), 'days') + 1);
        const previousEndDate = moment.utc(currentRangeStartDate).subtract(1, 'day').endOf('day');
        const previousStartDate = previousEndDate
            .clone()
            .subtract(days - 1, 'days')
            .startOf('day');
        return { start: previousStartDate, end: previousEndDate, days };
    }, [showComparison, currentRangeStartDate, currentRangeEndDate]);

    const currentLegendDateRange = useMemo(() => {
        if (selectedMetricChartData.length === 0) {
            return null;
        }
        const t0 = selectedMetricChartData[0].time;
        const t1 = selectedMetricChartData[selectedMetricChartData.length - 1].time;
        if (rangePreset === 'all') {
            return `${moment.utc(t0).format('MMM D, YYYY')} – ${moment.utc(t1).format('MMM D, YYYY')}`;
        }
        if (!currentRangeStartDate || !currentRangeEndDate) {
            return `${moment.utc(t0).format('MMM D, YYYY')} – ${moment.utc(t1).format('MMM D, YYYY')}`;
        }
        return `${moment.utc(currentRangeStartDate).format('MMM D, YYYY')} – ${moment.utc(currentRangeEndDate).format('MMM D, YYYY')}`;
    }, [rangePreset, selectedMetricChartData, currentRangeStartDate, currentRangeEndDate]);

    const comparisonLegendDateRange = useMemo(() => {
        if (!showComparison || !previousPeriodRange) {
            return null;
        }
        const { start, end, days } = previousPeriodRange;
        return {
            text: `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')} (Previous ${days} Day${days === 1 ? '' : 's'})`,
            vsLabel: `vs Previous ${days} Day${days === 1 ? '' : 's'}`,
        };
    }, [showComparison, previousPeriodRange]);

    const chartPeakLowStat = useMemo(() => {
        const pts = selectedMetricChartData;
        if (pts.length === 0) {
            return null;
        }
        let maxPt = pts[0];
        let minPt = pts[0];
        pts.forEach(p => {
            if (p.value > maxPt.value) {
                maxPt = p;
            }
            if (p.value < minPt.value) {
                minPt = p;
            }
        });
        return { maxPt, minPt };
    }, [selectedMetricChartData]);

    const periodChangePercent = useMemo(() => {
        if (!showComparison) {
            return null;
        }
        const key = chartMetricToTotalsKey[selectedChartMetric];
        const cur = totals[key];
        const prev = comparisonDisplay[key];
        if (!Number.isFinite(prev) || prev === 0 || !Number.isFinite(cur)) {
            return null;
        }
        return ((cur - prev) / prev) * 100;
    }, [showComparison, selectedChartMetric, totals, comparisonDisplay]);

    const trendStatsSurface = theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50];
    const analyticsReportExportData = useMemo(() => {
        if (!isReportFullyLoaded) {
            return [];
        }
        return selectedVisibleSubscribers.map(subscriber => {
            const id = String(subscriber._id);
            const m = metricsBySubscriberId[id] || zeroMetrics;
            const d = deltasBySubscriberId[id] || extractDeltas(null);
            const comparisonChargebacks = Number(comparisonChargebacksDataBySubscriberId[id]?.total || 0);
            const comparisonNetRevenue = safeTotal(comparisonEarningsDataBySubscriberId[id]?.total, 'net');
            const comparisonLtvRevenueBase = comparisonNetRevenue - comparisonChargebacks;
            const comparisonLtv = m.ltvUsersBase > 0 ? comparisonLtvRevenueBase / m.ltvUsersBase : 0;
            const withComparison = (row: Record<string, string | number>, key: string, currentValue: number, comparisonValue: number) => {
                if (!showComparison) {
                    return;
                }
                const deltaValue = currentValue - comparisonValue;
                row[`${key}Comparison`] = Number(comparisonValue || 0);
                row[`${key}Delta`] = Number(deltaValue || 0);
                row[`${key}DeltaPercent`] = comparisonValue !== 0 ? Number(((deltaValue / comparisonValue) * 100).toFixed(2)) : 0;
            };

            const row: Record<string, string | number> = {
                Username: subscriber.username || '',
                LoggedIn: Number(Boolean(subscriber.isLoggedIn)),
                ComparisonEnabled: Number(showComparison),
                Earnings: Number(m.earnings || 0),
                Chargebacks: Number(m.chargebacks || 0),
                LTV: Number(m.ltv || 0),
                Tips: Number(m.tips || 0),
                Messages: Number(m.messages || 0),
                Posts: Number(m.posts || 0),
                Subscriptions: Number(m.subscriptions || 0),
                StreamTips: Number(m.streamTips || 0),
                ReferralEarnings: Number(m.referralEarnings || 0),
                NewSubscribersToday: Number(m.newSubscribersToday || 0),
                ActiveFans: Number(m.activeFans || 0),
                ActiveFollowing: Number(m.activeFollowing || 0),
                PayoutAvailable: Number(m.payoutAvailable || 0),
                PayoutPending: Number(m.payoutPending || 0),
                ReferralsBalance: Number(m.referralsBalance || 0),
                StoriesToday: Number(m.todayStories || 0),
                PostsToday: Number(m.todayPosts || 0),
                ChatsToday: Number(m.todayChats || 0),
                StreamsToday: Number(m.todayStreams || 0),
                ProfileVisitors: Number(m.profileVisitorsTotal || 0),
                ProfileVisitorGuests: Number(m.profileVisitorsGuests || 0),
                ProfileVisitorUsers: Number(m.profileVisitorsUsers || 0),
                ProfileVisitorSubscribers: Number(m.profileVisitorsSubscribers || 0),
                PerformerRankPercent: Number(m.performerTop || 0),
            };
            withComparison(row, 'Earnings', m.earnings, m.earnings - d.earnings);
            withComparison(row, 'Chargebacks', m.chargebacks, comparisonChargebacks);
            withComparison(row, 'LTV', m.ltv, comparisonLtv);
            withComparison(row, 'Tips', m.tips, m.tips - d.tips);
            withComparison(row, 'Messages', m.messages, m.messages - d.messages);
            withComparison(row, 'Posts', m.posts, m.posts - d.posts);
            withComparison(row, 'Subscriptions', m.subscriptions, m.subscriptions - d.subscriptions);
            withComparison(row, 'StreamTips', m.streamTips, m.streamTips - d.streamTips);
            withComparison(row, 'ReferralEarnings', m.referralEarnings, m.referralEarnings - d.referralEarnings);
            withComparison(
                row,
                'ProfileVisitors',
                m.profileVisitorsTotal,
                safeNumber(comparisonProfileVisitorsDataBySubscriberId[id]?.topCountries?.totals?.total),
            );
            withComparison(
                row,
                'ProfileVisitorGuests',
                m.profileVisitorsGuests,
                safeNumber(comparisonProfileVisitorsDataBySubscriberId[id]?.topCountries?.totals?.guests),
            );
            withComparison(
                row,
                'ProfileVisitorUsers',
                m.profileVisitorsUsers,
                safeNumber(comparisonProfileVisitorsDataBySubscriberId[id]?.topCountries?.totals?.users),
            );
            withComparison(
                row,
                'ProfileVisitorSubscribers',
                m.profileVisitorsSubscribers,
                safeNumber(comparisonProfileVisitorsDataBySubscriberId[id]?.topCountries?.totals?.subscribers),
            );

            return row;
        });
    }, [
        selectedVisibleSubscribers,
        metricsBySubscriberId,
        deltasBySubscriberId,
        comparisonChargebacksDataBySubscriberId,
        comparisonEarningsDataBySubscriberId,
        comparisonProfileVisitorsDataBySubscriberId,
        showComparison,
        isReportFullyLoaded,
    ]);
    const chartExportData = useMemo(() => {
        if (!selectedMetricChartData || selectedMetricChartData.length === 0) {
            return [];
        }

        const comparisonByTime = new Map<number, number>();
        if (showComparison) {
            comparisonMetricChartData.forEach(point => {
                comparisonByTime.set(Number(point.time), Number(point.comparisonValue || 0));
            });
        }

        return selectedMetricChartData.map(point => {
            const row: Record<string, string | number> = {
                Date: moment.utc(Number(point.time)).format('YYYY-MM-DD'),
                Metric: chartMetricLabels[selectedChartMetric],
                Value: Number(point.value || 0),
            };

            if (showComparison) {
                const comparisonValue = Number(comparisonByTime.get(Number(point.time)) || 0);
                const deltaValue = Number(point.value || 0) - comparisonValue;
                row.ComparisonValue = comparisonValue;
                row.DeltaValue = deltaValue;
                row.DeltaPercent = comparisonValue !== 0 ? Number(((deltaValue / comparisonValue) * 100).toFixed(2)) : 0;
                row.CurrentPeriod = currentLegendDateRange || '';
                row.ComparisonPeriod = comparisonLegendDateRange?.text || '';
            }

            return row;
        });
    }, [
        selectedMetricChartData,
        comparisonMetricChartData,
        showComparison,
        selectedChartMetric,
        currentLegendDateRange,
        comparisonLegendDateRange,
    ]);
    const visitorsTrendChartExportData = useMemo(() => {
        if (profileVisitorsLoadedCount < maxCount || profileVisitorsChartData.length === 0) {
            return [];
        }

        const comparisonByTime = new Map<number, number>();
        if (showComparison) {
            profileVisitorsComparisonChartData.forEach(point => {
                comparisonByTime.set(Number(point.time), Number(point.comparisonValue || 0));
            });
        }

        return profileVisitorsChartData.map(point => {
            const row: Record<string, string | number> = {
                Date: moment.utc(Number(point.time)).format('YYYY-MM-DD'),
                Metric: 'Profile Visitors',
                Value: Number(point.value || 0),
            };

            if (showComparison) {
                const comparisonValue = Number(comparisonByTime.get(Number(point.time)) || 0);
                const deltaValue = Number(point.value || 0) - comparisonValue;
                row.ComparisonValue = comparisonValue;
                row.DeltaValue = deltaValue;
                row.DeltaPercent = comparisonValue !== 0 ? Number(((deltaValue / comparisonValue) * 100).toFixed(2)) : 0;
                row.CurrentPeriod = profileVisitorsCurrentLegendDateRange || '';
                row.ComparisonPeriod = profileVisitorsComparisonLegendDateRange || '';
            }

            return row;
        });
    }, [
        profileVisitorsLoadedCount,
        maxCount,
        profileVisitorsChartData,
        showComparison,
        profileVisitorsComparisonChartData,
        profileVisitorsCurrentLegendDateRange,
        profileVisitorsComparisonLegendDateRange,
    ]);

    const renderSubscriberRow = useCallback(
        (subscriber: OnlyFansSubscriber) => (
            <SubscriberAnalyticsRow
                key={String(subscriber._id)}
                subscriber={subscriber}
                onLoaded={onLoaded}
                onEarningsDataLoaded={onEarningsDataLoaded}
                onComparisonEarningsDataLoaded={onComparisonEarningsDataLoaded}
                onChargebacksDataLoaded={onChargebacksDataLoaded}
                onComparisonChargebacksDataLoaded={onComparisonChargebacksDataLoaded}
                onProfileVisitorsDataLoaded={onProfileVisitorsDataLoaded}
                onComparisonProfileVisitorsDataLoaded={onComparisonProfileVisitorsDataLoaded}
                checked={selectedBySubscriberId[String(subscriber._id)] !== false}
                onToggleChecked={onToggleSelected}
                amountType={amountType}
                rangePreset={rangePreset}
                comparisonPreset={comparisonPreset}
                customRangeStartDate={customRangeStartDate}
                customRangeEndDate={customRangeEndDate}
            />
        ),
        [
            onLoaded,
            onEarningsDataLoaded,
            onComparisonEarningsDataLoaded,
            onChargebacksDataLoaded,
            onComparisonChargebacksDataLoaded,
            onProfileVisitorsDataLoaded,
            onComparisonProfileVisitorsDataLoaded,
            selectedBySubscriberId,
            onToggleSelected,
            amountType,
            rangePreset,
            comparisonPreset,
            customRangeStartDate,
            customRangeEndDate,
        ],
    );

    const renderSubscriberCard = useCallback(
        (subscriber: OnlyFansSubscriber) => (
            <Grid item xs={12} sm={6} key={String(subscriber._id)}>
                <SubscriberAnalyticsCard
                    subscriber={subscriber}
                    onLoaded={onLoaded}
                    onEarningsDataLoaded={onEarningsDataLoaded}
                    onComparisonEarningsDataLoaded={onComparisonEarningsDataLoaded}
                    onChargebacksDataLoaded={onChargebacksDataLoaded}
                    onComparisonChargebacksDataLoaded={onComparisonChargebacksDataLoaded}
                    onProfileVisitorsDataLoaded={onProfileVisitorsDataLoaded}
                    onComparisonProfileVisitorsDataLoaded={onComparisonProfileVisitorsDataLoaded}
                    checked={selectedBySubscriberId[String(subscriber._id)] !== false}
                    onToggleChecked={onToggleSelected}
                    amountType={amountType}
                    rangePreset={rangePreset}
                    comparisonPreset={comparisonPreset}
                    customRangeStartDate={customRangeStartDate}
                    customRangeEndDate={customRangeEndDate}
                />
            </Grid>
        ),
        [
            onLoaded,
            onEarningsDataLoaded,
            onComparisonEarningsDataLoaded,
            onChargebacksDataLoaded,
            onComparisonChargebacksDataLoaded,
            onProfileVisitorsDataLoaded,
            onComparisonProfileVisitorsDataLoaded,
            selectedBySubscriberId,
            onToggleSelected,
            amountType,
            rangePreset,
            comparisonPreset,
            customRangeStartDate,
            customRangeEndDate,
        ],
    );

    const renderSubscribers = (items: OnlyFansSubscriber[]) => {
        const selectedCount = items.filter(subscriber => selectedBySubscriberId[String(subscriber._id)] !== false).length;
        const allSelected = items.length > 0 && selectedCount === items.length;
        const partiallySelected = selectedCount > 0 && selectedCount < items.length;

        if (isLargeScreen) {
            return (
                <SubscriberAnalyticsTableSection
                    items={items}
                    allSelected={allSelected}
                    partiallySelected={partiallySelected}
                    onSetSelectedForGroup={onSetSelectedForGroup}
                    analyticsTableSurfaceHeaderBg={analyticsTableSurface.headerBg}
                    renderRow={renderSubscriberRow}
                />
            );
        }

        return (
            <SubscriberAnalyticsCardsSection
                items={items}
                allSelected={allSelected}
                partiallySelected={partiallySelected}
                onSetSelectedForGroup={onSetSelectedForGroup}
                renderCard={renderSubscriberCard}
            />
        );
    };

    return (
        <PageContainer>
            <Stack spacing={1}>
                <Typography variant="h6">
                    <BackNavigationButton url={`/`} />
                    Report Card
                </Typography>
                <Box>
                    <AnalyticsControlsBar
                        isLargeScreen={isLargeScreen}
                        rangePreset={rangePreset}
                        comparisonPreset={comparisonPreset}
                        amountType={amountType}
                        activeMainRangeLabel={activeMainRangeLabel}
                        onRangePresetChange={setRangePreset}
                        onOpenCustomRangeDialog={openCustomRangeDialog}
                        onComparisonPresetChange={setComparisonPreset}
                        onAmountTypeChange={setAmountType}
                        analyticsReportExportData={analyticsReportExportData}
                        reportFilename={`analytics_report_${amountType}_${rangePreset}`}
                    />
                </Box>
                <Box>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<AttachMoneyIcon color="success" />}
                                title="Total Earnings"
                                value={formatUsdMoney(display.earnings)}
                                delta={showComparison ? totalDeltas.earnings : undefined}
                                deltaLabel="vs Previous Period"
                                progressCurrent={earningsLoadedCount}
                                progressMax={maxCount}
                                selected={selectedChartMetric === 'earnings'}
                                onClick={() => setSelectedChartMetric('earnings')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<MoneyOffIcon color="error" />}
                                title="Total Chargebacks"
                                value={formatUsdMoney(display.chargebacks)}
                                delta={showComparison ? display.chargebacks - comparisonDisplay.chargebacks : undefined}
                                deltaLabel="vs Previous Period"
                                progressCurrent={chargebacksLoadedCount}
                                progressMax={maxCount}
                                selected={selectedChartMetric === 'chargebacks'}
                                onClick={() => setSelectedChartMetric('chargebacks')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<AssessmentIcon color="warning" />}
                                title="Total Tips"
                                value={formatUsdMoney(display.tips)}
                                delta={showComparison ? totalDeltas.tips : undefined}
                                deltaLabel="vs Previous Period"
                                progressCurrent={earningsLoadedCount}
                                progressMax={maxCount}
                                selected={selectedChartMetric === 'tips'}
                                onClick={() => setSelectedChartMetric('tips')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<MailOutlineIcon color="info" />}
                                title="Total Messages"
                                value={formatUsdMoney(display.messages)}
                                delta={showComparison ? totalDeltas.messages : undefined}
                                deltaLabel="vs Previous Period"
                                progressCurrent={earningsLoadedCount}
                                progressMax={maxCount}
                                selected={selectedChartMetric === 'messages'}
                                onClick={() => setSelectedChartMetric('messages')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<PostAddIcon color="secondary" />}
                                title="Total Posts"
                                value={formatUsdMoney(display.posts)}
                                delta={showComparison ? totalDeltas.posts : undefined}
                                deltaLabel="vs Previous Period"
                                progressCurrent={earningsLoadedCount}
                                progressMax={maxCount}
                                selected={selectedChartMetric === 'posts'}
                                onClick={() => setSelectedChartMetric('posts')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<SubscriptionsIcon color="primary" />}
                                title="Total Subscriptions"
                                value={formatUsdMoney(display.subscriptions)}
                                delta={showComparison ? totalDeltas.subscriptions : undefined}
                                deltaLabel="vs Previous Period"
                                progressCurrent={earningsLoadedCount}
                                progressMax={maxCount}
                                selected={selectedChartMetric === 'subscriptions'}
                                onClick={() => setSelectedChartMetric('subscriptions')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<DynamicFeedIcon color="info" />}
                                title="Total Stream Tips"
                                value={formatUsdMoney(display.streamTips)}
                                delta={showComparison ? totalDeltas.streamTips : undefined}
                                deltaLabel="vs Previous Period"
                                progressCurrent={earningsLoadedCount}
                                progressMax={maxCount}
                                selected={selectedChartMetric === 'streamTips'}
                                onClick={() => setSelectedChartMetric('streamTips')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<AttachMoneyIcon color="secondary" />}
                                title="Total Referral Earnings"
                                value={formatUsdMoney(display.referralEarnings)}
                                delta={showComparison ? totalDeltas.referralEarnings : undefined}
                                deltaLabel="vs Previous Period"
                                progressCurrent={earningsLoadedCount}
                                progressMax={maxCount}
                                selected={selectedChartMetric === 'referralEarnings'}
                                onClick={() => setSelectedChartMetric('referralEarnings')}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <EarningsTrendCard
                    chartMetricLabel={chartMetricLabels[selectedChartMetric]}
                    chartExportData={chartExportData}
                    chartFilename={`analytics_chart_${selectedChartMetric}_${amountType}_${rangePreset}`}
                    selectedMetricChartData={selectedMetricChartData}
                    comparisonMetricChartData={comparisonMetricChartData}
                    yAxisDomain={yAxisDomain}
                    rangePreset={rangePreset}
                    currentLegendDateRange={currentLegendDateRange}
                    showComparison={showComparison}
                    comparisonLegendDateRange={comparisonLegendDateRange}
                    chartPeakLowStat={chartPeakLowStat}
                    periodChangePercent={periodChangePercent}
                    peakLabel={chartPeakLowLabels[selectedChartMetric].peak}
                    lowestLabel={chartPeakLowLabels[selectedChartMetric].lowest}
                />

                <Box>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<AttachMoneyIcon color="success" />}
                                title="Total LTV"
                                value={formatUsdMoney(display.ltv)}
                                inlineComparisonValue={showComparison ? formatUsdMoney(comparisonLtv) : undefined}
                                inlineComparisonDelta={showComparison ? display.ltv - comparisonLtv : undefined}
                                progressCurrent={earningsLoadedCount}
                                progressMax={maxCount}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<PersonAddAlt1Icon color="info" />}
                                title="Subscribers Today"
                                value={display.newSubscribersToday.toLocaleString()}
                                progressCurrent={newSubscribersLoadedCount}
                                progressMax={maxCount}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<GroupIcon color="info" />}
                                title="Fans"
                                value={display.activeFans.toLocaleString()}
                                progressCurrent={usersCountersLoadedCount}
                                progressMax={maxCount}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<SubscriptionsIcon color="info" />}
                                title="Following"
                                value={display.activeFollowing.toLocaleString()}
                                progressCurrent={usersCountersLoadedCount}
                                progressMax={maxCount}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<AttachMoneyIcon color="success" />}
                                title="Payout Available"
                                value={formatUsdMoney(display.payoutAvailable)}
                                progressCurrent={payoutsBalanceLoadedCount}
                                progressMax={maxCount}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<AttachMoneyIcon color="warning" />}
                                title="Payout Pending"
                                value={formatUsdMoney(display.payoutPending)}
                                progressCurrent={payoutsBalanceLoadedCount}
                                progressMax={maxCount}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<AttachMoneyIcon color="secondary" />}
                                title="Referrals Balance"
                                value={formatUsdMoney(display.referralsBalance)}
                                progressCurrent={referralsBalanceLoadedCount}
                                progressMax={maxCount}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DashboardAnalyticsSummaryStatCard
                                icon={<AssessmentIcon color="secondary" />}
                                title="Avg Performer Rank"
                                value={`${averagePerformerTop.toFixed(2)}%`}
                                progressCurrent={performerTopLoadedCount}
                                progressMax={maxCount}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <ProfileVisitorsTrendCard
                    profileVisitorsLoadedCount={profileVisitorsLoadedCount}
                    maxCount={maxCount}
                    visitorsTrendChartExportData={visitorsTrendChartExportData}
                    exportFilename={`analytics_profile_visitors_chart_${rangePreset}`}
                    profileVisitorsChartData={profileVisitorsChartData}
                    profileVisitorsComparisonChartData={profileVisitorsComparisonChartData}
                    profileVisitorsYAxisDomain={profileVisitorsYAxisDomain}
                    rangePreset={rangePreset}
                    profileVisitorsCurrentLegendDateRange={profileVisitorsCurrentLegendDateRange}
                    profileVisitorsComparisonLegendDateRange={profileVisitorsComparisonLegendDateRange}
                    showComparison={showComparison}
                    profileVisitorsPeakLowStat={profileVisitorsPeakLowStat}
                    profileVisitorsPeriodChangePercent={profileVisitorsPeriodChangePercent}
                />

                <Box>
                    <ProfileVisitorsSummaryCard
                        profileVisitorsLoadedCount={profileVisitorsLoadedCount}
                        maxCount={maxCount}
                        showComparison={showComparison}
                        display={display}
                        comparisonProfileVisitorsTotals={comparisonProfileVisitorsTotals}
                        hasData={profileVisitorsCountriesSummary.length > 0}
                    />
                </Box>

                <ProfileVisitorsCountriesCard
                    profileVisitorsLoadedCount={profileVisitorsLoadedCount}
                    maxCount={maxCount}
                    profileVisitorsCountriesSummary={profileVisitorsCountriesSummary}
                    profileVisitorsCountriesTotal={profileVisitorsCountriesTotal}
                    analyticsTableSurfaceHeaderBg={analyticsTableSurface.headerBg}
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'stretch',
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                        borderRadius: theme => theme.shape.borderRadius,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            py: 2,
                            px: 2,
                            textAlign: 'center',
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                right: { xs: 14, sm: 0 },
                                left: { xs: 14, sm: 'auto' },
                                top: { xs: 'auto', sm: 14 },
                                bottom: { xs: 0, sm: 14 },
                                width: { xs: 'auto', sm: '1px' },
                                height: { xs: '1px', sm: 'auto' },
                                bgcolor: 'divider',
                            },
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" display="block">
                            Stories Today
                        </Typography>
                        {todayActivityLoadedCount < maxCount ? (
                            <Typography variant="h6" sx={{ mt: 0.75, fontWeight: 700, color: 'text.secondary' }}>
                                ...
                            </Typography>
                        ) : (
                            <Typography
                                variant="h6"
                                color="primary.main"
                                sx={{ mt: 0.75, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                            >
                                {display.todayStories.toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            py: 2,
                            px: 2,
                            textAlign: 'center',
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                right: { xs: 14, sm: 0 },
                                left: { xs: 14, sm: 'auto' },
                                top: { xs: 'auto', sm: 14 },
                                bottom: { xs: 0, sm: 14 },
                                width: { xs: 'auto', sm: '1px' },
                                height: { xs: '1px', sm: 'auto' },
                                bgcolor: 'divider',
                            },
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" display="block">
                            Posts Today
                        </Typography>
                        {todayActivityLoadedCount < maxCount ? (
                            <Typography variant="h6" sx={{ mt: 0.75, fontWeight: 700, color: 'text.secondary' }}>
                                ...
                            </Typography>
                        ) : (
                            <Typography
                                variant="h6"
                                color="primary.main"
                                sx={{ mt: 0.75, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                            >
                                {display.todayPosts.toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            py: 2,
                            px: 2,
                            textAlign: 'center',
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                right: { xs: 14, sm: 0 },
                                left: { xs: 14, sm: 'auto' },
                                top: { xs: 'auto', sm: 14 },
                                bottom: { xs: 0, sm: 14 },
                                width: { xs: 'auto', sm: '1px' },
                                height: { xs: '1px', sm: 'auto' },
                                bgcolor: 'divider',
                            },
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" display="block">
                            Chats Today
                        </Typography>
                        {todayActivityLoadedCount < maxCount ? (
                            <Typography variant="h6" sx={{ mt: 0.75, fontWeight: 700, color: 'text.secondary' }}>
                                ...
                            </Typography>
                        ) : (
                            <Typography
                                variant="h6"
                                color="primary.main"
                                sx={{ mt: 0.75, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                            >
                                {display.todayChats.toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, py: 2, px: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" display="block">
                            Streams Today
                        </Typography>
                        {todayActivityLoadedCount < maxCount ? (
                            <Typography variant="h6" sx={{ mt: 0.75, fontWeight: 700, color: 'text.secondary' }}>
                                ...
                            </Typography>
                        ) : (
                            <Typography
                                variant="h6"
                                color="primary.main"
                                sx={{ mt: 0.75, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                            >
                                {display.todayStreams.toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box>
                    <Grid container spacing={0.5} alignItems="center">
                        <Grid item xs>
                            <FilterTextFiled filter={filter} setFilter={setFilter} />
                        </Grid>
                        <Grid item xs="auto">
                            <DashboardMyAccountsSortMenuButton
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                            />
                        </Grid>
                        <Grid item xs="auto">
                            <GroupByCategoryToggle groupByCategory={groupByCategory} setGroupByCategory={setGroupByCategory} />
                        </Grid>
                    </Grid>
                </Box>

                <Box>
                    {isLoading ? (
                        <Skeleton />
                    ) : subscribers && Array.isArray(subscribers) && subscribers.length > 0 ? (
                        groupByCategory ? (
                            <>
                                {sortedUncategorized.length > 0 ? renderSubscribers(sortedUncategorized) : null}
                                {groupedData.uniqueCategories.map(category => (
                                    <React.Fragment key={category._id}>
                                        <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                                            {category.name}
                                        </Typography>
                                        {renderSubscribers(sortedByCategoryMap[category._id!] || [])}
                                    </React.Fragment>
                                ))}
                            </>
                        ) : (
                            renderSubscribers(sortedAll)
                        )
                    ) : (
                        <Alert severity="warning">No accounts are currently setup.</Alert>
                    )}
                </Box>
                <Dialog
                    open={isCustomRangeDialogOpen}
                    onClose={() => setIsCustomRangeDialogOpen(false)}
                    maxWidth={isDesktopRangePicker ? 'md' : 'sm'}
                    fullWidth
                >
                    <DialogContent sx={{ px: { xs: 1, sm: 2 }, overflowX: 'hidden' }}>
                        <Box display="flex" justifyContent="center" sx={{ width: '100%' }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <StaticDateRangePicker
                                    displayStaticWrapperAs={isDesktopRangePicker ? 'desktop' : 'mobile'}
                                    value={draftCustomRange}
                                    onChange={value => setDraftCustomRange(value)}
                                    slotProps={{ actionBar: { actions: [] } }}
                                    calendars={isDesktopRangePicker ? 2 : 1}
                                    sx={{
                                        maxWidth: '100%',
                                        '& .MuiPickersLayout-root': { minWidth: 0 },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsCustomRangeDialogOpen(false)} color="inherit">
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={applyCustomRange} disabled={!draftCustomRange[0] || !draftCustomRange[1]}>
                            Apply
                        </Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        </PageContainer>
    );
};

export default DashboardAnalytics;
